import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isNative } from '../utils/capacitor';
import { useConfirm, useAlert } from '../components/ConfirmModal';
import StoryRevealAnimation from '../components/StoryRevealAnimation';
import NextChoicesPanel from '../components/NextChoicesPanel';
import TomorrowPreviewCard from '../components/TomorrowPreviewCard';
import GrandparentShareModal from '../components/GrandparentShareModal';
import WishlistButton from '../components/WishlistButton';
import BedtimeCard from '../components/BedtimeCard';
import ReflectionPrompts from '../components/ReflectionPrompts';
import SeriesContinuationCard from '../components/SeriesContinuationCard';
import useLimits from '../utils/useLimits';
import TopUpModal from '../components/TopUpModal';
import StoryCardModal from '../components/StoryCardModal';
import {
  trackStoryRead, trackStoryFavorited, trackStoryShared,
  trackCommunityPublish, trackNarrationPlayed,
  trackQuizSubmitted, trackIllustrationDownloaded,
  trackIllustrationRegenerated, trackStoryDeleted, trackReadAloud, trackPdfExported } from '../utils/analytics';

const LANG_FLAGS = { en:'🇬🇧', es:'🇪🇸', fr:'🇫🇷', hi:'🇮🇳', gu:'🇮🇳', de:'🇩🇪', pt:'🇧🇷', ar:'🇸🇦', zh:'🇨🇳', ja:'🇯🇵', it:'🇮🇹' };
const LANG_NAMES = { en:'English', es:'Spanish', fr:'French', hi:'Hindi', gu:'Gujarati', de:'German', pt:'Portuguese', ar:'Arabic', zh:'Chinese', ja:'Japanese', it:'Italian' };
// Shortest silent MP3 as a data URI — used to unlock audio within the user gesture tap.
const SILENT_MP3 = 'data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsgU291bmQgRWZmZWN0cwBQUk9NAAAAAQAAAFRJVDIAAAAGAAABY29tbWVudAAAAQAAAFRSQ0sAAAAGAAABY29tbWVudAAAAQAAAFRBTEIAAAAGAAABY29tbWVudAAAAQAAAFRQRTEAAAAGAAABY29tbWVudAAAAQAAAFRDT04AAAAGAAABYwD/+9DEAAAJYAE3gAAAI0gB57AAAAAMQW1hbgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//vQxAAABiABH4AAAItQAe5AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import DashboardLayout from '../components/shared/DashboardLayout';
import Confetti from '../components/Confetti';
import recordActivity from '../utils/activity';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import useSettings from '../utils/useSettings';
import API from '../utils/api';
import { copyToClipboard } from '../utils/clipboard';
import { isFamilyEntitled, isViaCoParent, entitledPlan } from '../utils/entitlements';
import { saveOrShareFile } from '../utils/saveFile';

// ── Confetti burst for first story celebration ────────────────────────────────
function ConfettiBurst() {
  const pieces = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    color: ['#6f42c1','#f9c74f','#4ade80','#fb923c','#38bdf8','#f472b6','#a78bfa'][i % 7],
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 0.7}s`,
    duration: `${1 + Math.random() * 0.8}s`,
    size: `${6 + Math.random() * 8}px`,
    rotate: `${Math.random() * 720}deg`,
  }));
  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, pointerEvents:'none', zIndex:9999, overflow:'hidden' }}>
      <style>{`@keyframes cfall{0%{transform:translateY(-20px) rotate(0deg);opacity:1}100%{transform:translateY(110vh) rotate(var(--r));opacity:0}}`}</style>
      {pieces.map(p => (
        <div key={p.id} style={{
          position:'absolute', top:'-20px', left:p.left,
          width:p.size, height:p.size, background:p.color, borderRadius:'2px',
          '--r': p.rotate,
          animation:`cfall ${p.duration} ${p.delay} ease-in forwards`,
        }} />
      ))}
    </div>
  );
}

// Star rating labels for child feedback
const RATING_LABELS = { 1: '😴 Not great', 2: '😐 It was okay', 3: '🙂 Good', 4: '😄 Loved it!', 5: '🤩 Favourite!' };

// Reading-count milestones that trigger the completion celebration
const READ_MILESTONES = [1, 5, 10, 25, 50, 75, 100, 150, 200, 250, 300];
const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Illustration style options shown in the style selector
const ILLUSTRATION_STYLE_OPTIONS = [
  { value: 'watercolor', label: 'Watercolor', icon: '🎨' },
  { value: 'cartoon',    label: 'Cartoon',    icon: '✏️' },
  { value: 'storybook',  label: 'Storybook',  icon: '📖' },
  { value: 'realistic',  label: 'Realistic',  icon: '🖼️' },
  { value: 'anime',      label: 'Anime',      icon: '✨' },
];

// Given audio currentTime + duration, return which paragraph index is being narrated.
// Uses character-proportion to map time → story paragraph (title + content + moral).
const getActiveParagraphIdx = (currentTime, duration, paragraphs, titleLen, moralLen) => {
  if (!duration || duration <= 0 || !paragraphs.length) return -1;
  const paraChars = paragraphs.reduce((s, p) => s + p.length, 0);
  const totalChars = titleLen + 2 + paraChars + (moralLen ? moralLen + 2 : 0);
  const currentChar = (currentTime / duration) * totalChars;
  let pos = titleLen + 2; // skip title + '. '
  for (let i = 0; i < paragraphs.length; i++) {
    pos += paragraphs[i].length;
    if (currentChar <= pos) return i;
    pos += 2; // paragraph separator (\n\n → '. ', same length)
  }
  return paragraphs.length - 1;
};

const formatTime = (secs) => {
  const m = Math.floor((secs || 0) / 60);
  const s = Math.floor((secs || 0) % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// iOS Safari's speechSynthesis.pause() is broken — cancel+restart at last position instead.
const isIOSSpeech = () =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

const StoryReaderPage = () => {
  const confirm = useConfirm();
  const alert = useAlert();
  const { darkMode } = useTheme();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = searchParams.get('new') === 'true';
  const isFirstStory = searchParams.get('firstStory') === 'true';
  const isGenerating = searchParams.get('generating') === 'true';
  const [showReveal, setShowReveal] = useState(isNew && !isFirstStory);
  const [showGrandparentModal, setShowGrandparentModal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [genFailed, setGenFailed]   = useState(false);
  const genPollRef  = useRef(null);
  const genMsgIdx   = useRef(0);
  const GEN_MESSAGES = [
    'Weaving your story…',
    'Adding a sprinkle of magic…',
    'Writing the exciting scenes…',
    'Giving your characters their voices…',
    'Polishing every sentence…',
    'Almost there — finishing touches…',
  ];
  const [genMsg, setGenMsg] = useState(GEN_MESSAGES[0]);
  const [fontSize, setFontSize] = useState(18);

  // Browser read-aloud (free)
  // On Android Capacitor: uses @capacitor-community/text-to-speech (native)
  // On web: uses window.speechSynthesis with async voice loading
  const [speaking, setSpeaking] = useState(false);
  const [speechLoading, setSpeechLoading] = useState(false);
  const [speechError, setSpeechError] = useState(null);
  const utteranceRef = useRef(null);
  // Native TTS state refs (Android Capacitor only)
  const nativeTtsParagraphRef = useRef(0);  // current paragraph index
  const nativeTtsPausedRef    = useRef(false);
  const nativeTtsStoppedRef   = useRef(false);

  // PDF export
  const [exporting, setExporting] = useState(false);

  // Sharing
  const [showShareModal, setShowShareModal]   = useState(false);
  const [showQuickShare, setShowQuickShare]   = useState(false);
  const [shareLink, setShareLink]             = useState(null);
  const [shareForm, setShareForm]             = useState({ expiryDays: '30', password: '' });
  const [shareLoading, setShareLoading]       = useState(false);
  const [shareCopied, setShareCopied]         = useState(false);
  const [quickShareCopied, setQuickShareCopied] = useState(false);

  // Smart narration (basic/family)
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPlaying, setAiPlaying] = useState(false);
  const audioRef = useRef(null);
  const audioObjRef = useRef(null);

  // Cloned-voice narration (family)
  const [voiceProfile, setVoiceProfile] = useState(null);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [clonePlaying, setClonePlaying] = useState(false);
  const cloneAudioRef = useRef(null);
  const cloneAudioObjRef = useRef(null);

  // Narration enhancement — speed, pause/resume, sentence highlight, resume position
  const [playbackSpeed, setPlaybackSpeed] = useState(1);      // 0.75 | 1 | 1.25
  const [aiPaused, setAiPaused] = useState(false);
  const [clonePaused, setClonePaused] = useState(false);
  const [activeSentenceIdx, setActiveSentenceIdx] = useState(-1);
  const [narrationDuration, setNarrationDuration] = useState(0);
  const [narrationCurrentTime, setNarrationCurrentTime] = useState(0);
  const [speechPaused, setSpeechPaused] = useState(false);
  const sentenceTimingsRef = useRef([]);
  const speechSentencePosRef = useRef([]); // [{start,end}] char positions in utterance
  const speechLastCharRef = useRef(0);     // absolute charIndex at last boundary event
  const speechTextOffsetRef = useRef(0);   // offset when speech was restarted (iOS resume)
  const speechFullTextRef = useRef('');    // full clean text, kept for iOS slice-on-resume
  const speechResumeIntervalRef = useRef(null); // iOS 15s anti-pause interval for read-aloud
  const resumePositionRef = useRef(0); // seek target applied when audio loads
  const aiSaveTimerRef = useRef(null);
  const cloneSaveTimerRef = useRef(null);

  // Illustration
  const [illustration, setIllustration] = useState(null);       // url
  const [illustrationStatus, setIllustrationStatus] = useState('none');
  const [illustrationStyle, setIllustrationStyle] = useState('watercolor');
  const [regenLoading, setRegenLoading] = useState(false);
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  const mountedWithGenerating = useRef(false);
  const [illustrationStuck, setIllustrationStuck] = useState(false);
  const [illustrationSlow, setIllustrationSlow] = useState(false);

  // Quiz
  const [quiz, setQuiz] = useState(null);           // array of questions
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});   // { [questionIndex]: chosenOptionIndex }
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [previousScore, setPreviousScore] = useState(null); // { score, total } from a prior attempt
  const quizSectionRef = useRef(null);

  // Story continuations — "What happens next?" chapters
  const [continuations, setContinuations] = useState([]);
  const [contGenerating, setContGenerating] = useState(false);
  const [contError, setContError] = useState(null);
  const contPollRef = useRef(null);

  // Story card
  const [showCardModal, setShowCardModal] = useState(false);

  // WhatsApp/share prompt (shown once on new story, dismissible)
  const [sharePromptDismissed, setSharePromptDismissed] = useState(false);
  const [sharePromptCopied, setSharePromptCopied] = useState(false);

  // Child feedback — star rating
  const [storyRating, setStoryRating] = useState(null);     // 1–5 once submitted
  const [ratingHover, setRatingHover] = useState(0);        // star being hovered
  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  // Session-ending bedtime card
  const [showBedtimeCard, setShowBedtimeCard] = useState(false);
  const [seriesContinuationDismissed, setSeriesContinuationDismissed] = useState(false);

  // Story completion celebration
  const [readMilestone, setReadMilestone] = useState(null); // e.g. 50 → "50th story!"
  const [tomorrowIdea, setTomorrowIdea] = useState(null);   // tomorrow's recommended concept
  const [celebrate, setCelebrate] = useState(false);        // confetti (milestone / perfect quiz)
  const [levelUp, setLevelUp] = useState(null);             // adaptive reading-level change

  const startCelebration = () => {
    setCelebrate(true);
    setTimeout(() => setCelebrate(false), 5000);
  };

  // ── Activity tracking (lifetime event store) ──
  // Read duration: time between opening and leaving the story
  const readStartRef = useRef(Date.now());
  const readSentRef = useRef(false);
  // Narration: highest % reached this page view, and which player it was
  const maxNarrationPctRef = useRef(0);
  const narrationTypeRef = useRef(null);

  useEffect(() => () => {
    if (!readSentRef.current) {
      readSentRef.current = true;
      const seconds = Math.min(3600, Math.round((Date.now() - readStartRef.current) / 1000));
      if (seconds >= 10) recordActivity('story.read_duration', { storyId: id, seconds });
    }
    if (maxNarrationPctRef.current >= 5) {
      recordActivity('narration.played', { storyId: id, type: narrationTypeRef.current, percent: maxNarrationPctRef.current });
    }
    if (contPollRef.current) clearInterval(contPollRef.current);
    // eslint-disable-next-line
  }, []);

  // Track the furthest point reached in the narration (both audio players
  // share these position states)
  useEffect(() => {
    if (narrationDuration > 0 && narrationCurrentTime > 0) {
      const pct = Math.min(100, Math.round((narrationCurrentTime / narrationDuration) * 100));
      if (pct > maxNarrationPctRef.current) maxNarrationPctRef.current = pct;
      narrationTypeRef.current = clonePlaying ? 'clone' : 'ai';
    }
    // eslint-disable-next-line
  }, [narrationCurrentTime, narrationDuration]);

  const handleReadResponse = (res) => {
    const { newlyRead, readStoriesCount } = res.data || {};
    if (newlyRead && READ_MILESTONES.includes(readStoriesCount)) {
      setReadMilestone(readStoriesCount);
      startCelebration();
    }
  };

  // Community publish
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishName, setPublishName] = useState('');
  const [publishLoading, setPublishLoading] = useState(false);

  const [showTopUp, setShowTopUp]     = useState(false);
  const [topUpType, setTopUpType]     = useState(null);

  // Trigger confetti once on first-story arrival, then auto-dismiss after 3s
  useEffect(() => {
    if (!isFirstStory) return;
    setShowConfetti(true);
    const t = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(t);
  }, [isFirstStory]);

  const navigate = useNavigate();
  const { user } = useAuth();
  const hasPaidPlan = entitledPlan(user) !== 'free';
  // isFamilyEntitled reads the server-computed entitlement, so a co-parent on a
  // Family plan is included. Deriving it from user.subscription.plan here meant
  // co-parents never saw the clone-narrate button or the advanced share options,
  // even though the API grants both to them.
  const isFamily = isFamilyEntitled(user);
  const { remaining } = useLimits();
  const { settings } = useSettings();
  const flags = settings.featureFlags || {};

  // ── Poll /stories/:id/status when arriving from generate with ?generating=true ──
  // Once generationStatus flips to 'ready', fetch the full story and render normally.
  // Max 90 polls × 3s = 270s (4.5 min) which covers even slow photo+face-swap stories.
  useEffect(() => {
    if (!isGenerating) return;
    let polls = 0;
    const MAX_POLLS = 90;
    genPollRef.current = setInterval(async () => {
      // Rotate the loading message every 2 polls (~6s)
      genMsgIdx.current = (genMsgIdx.current + 1) % GEN_MESSAGES.length;
      setGenMsg(GEN_MESSAGES[genMsgIdx.current]);

      polls += 1;
      if (polls > MAX_POLLS) {
        clearInterval(genPollRef.current);
        setGenFailed(true);
        setLoading(false);
        return;
      }
      try {
        const res = await API.get(`/stories/${id}/status`);
        if (res.data.generationStatus === 'ready') {
          clearInterval(genPollRef.current);
          const s = res.data.story;
          setStory(s);
          setIllustration(s.illustrationUrl || null);
          setIllustrationStyle(s.illustrationStyle || 'watercolor');
          setStoryRating(s.feedback?.rating || null);
          if (s.quiz?.length > 0) setQuiz(s.quiz);
          if (s.quizScore != null && s.quizTotal != null) setPreviousScore({ score: s.quizScore, total: s.quizTotal });
          if (s.continuations?.length) setContinuations(s.continuations);
          // Illustration pipeline fires separately — treat as fresh story
          mountedWithGenerating.current = false;
          setIllustrationStuck(false);
          setIllustrationStatus(s.illustrationStatus === 'none' ? 'generating' : s.illustrationStatus);
          setLoading(false);
          trackStoryRead(id);
          // Mark as read (same as the normal fetch path)
          const readKey = `read_story_${id}`;
          if (!sessionStorage.getItem(readKey)) {
            sessionStorage.setItem(readKey, '1');
            API.put(`/stories/${id}/read`).then(handleReadResponse).catch(() => {});
          }
        } else if (res.data.generationStatus === 'failed') {
          clearInterval(genPollRef.current);
          setGenFailed(true);
          setLoading(false);
        }
      } catch { /* keep polling on transient network errors */ }
    }, 3000);
    return () => clearInterval(genPollRef.current);
  }, []); // eslint-disable-line

  useEffect(() => {
    // Skip the normal initial fetch when we arrived via ?generating=true —
    // the polling effect above will load the story once it's ready.
    if (isGenerating) return;

    API.get(`/stories/${id}`).then(res => {
      const s = res.data.story;
      setStory(s);
      // Restore saved resume position so first play-press resumes from where they left off
      if (s.narrationPosition > 1) resumePositionRef.current = s.narrationPosition;
      setIllustration(s.illustrationUrl || null);
      setIllustrationStyle(s.illustrationStyle || 'watercolor');
      setStoryRating(s.feedback?.rating || null);
      if (s.quiz?.length > 0) setQuiz(s.quiz);
      if (s.quizScore != null && s.quizTotal != null) setPreviousScore({ score: s.quizScore, total: s.quizTotal });
      if (s.continuations?.length) setContinuations(s.continuations);
      const loadedStatus = s.illustrationStatus || 'none';
      if (isNew && (loadedStatus === 'none' || loadedStatus === 'generating')) {
        mountedWithGenerating.current = false;
        setIllustrationStuck(false);
        setIllustrationStatus('generating');
      } else if (!isNew && loadedStatus === 'generating') {
        mountedWithGenerating.current = true;
        setIllustrationStuck(false);
        setIllustrationStatus('generating');
      } else {
        setIllustrationStatus(loadedStatus);
      }
      const readKey = `read_story_${id}`;
      if (!sessionStorage.getItem(readKey)) {
        sessionStorage.setItem(readKey, '1');
        API.put(`/stories/${id}/read`).then(handleReadResponse).catch(() => {});
      }
      trackStoryRead(id);
    }).catch(() => navigate('/dashboard/library')).finally(() => setLoading(false));
    const stopAll = () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      if (audioObjRef.current) { audioObjRef.current.pause(); audioObjRef.current = null; }
      if (cloneAudioObjRef.current) { cloneAudioObjRef.current.pause(); cloneAudioObjRef.current = null; }
      clearInterval(aiSaveTimerRef.current);
      clearInterval(cloneSaveTimerRef.current);
      // The read-aloud keep-alive was missing here, so it survived unmount and
      // kept firing every 10s for the life of the tab — one orphan per story
      // opened with read-aloud. CommunityStoryPage, which has the same speech
      // controls, already clears it in its own unmount path.
      if (speechResumeIntervalRef.current) {
        clearInterval(speechResumeIntervalRef.current);
        speechResumeIntervalRef.current = null;
      }
    };
    window.addEventListener('beforeunload', stopAll);
    return () => {
      stopAll();
      window.removeEventListener('beforeunload', stopAll);
    };
  }, [id]);

  // Fetch voice profile for Family plan users
  useEffect(() => {
    if (isFamily) {
      API.get('/voice/profile')
        .then(res => { if (res.data.profile) setVoiceProfile(res.data.profile); })
        .catch(() => {});
    }
  }, [isFamily]);

  // Poll illustration status until done or failed.
  // Two timeout tiers:
  //   • Fresh story (?new=true): 120s (30 polls × 4s) — covers all illustration types:
  //     DALL-E 3 alone (~10-15s), photo mode with face-swap (~30-90s).
  //     Also handles the race where status is still 'none' when page first loads.
  //   • Stale 'generating' from a previous session (server restarted, task is gone):
  //     give it 20s (5 polls × 4s) to self-recover, then show "Regenerate for free".
  useEffect(() => {
    if (['done', 'done_no_face', 'failed', 'none'].includes(illustrationStatus)) {
      clearInterval(pollRef.current);
      pollCountRef.current = 0;
      setIllustrationStuck(false);
      setIllustrationSlow(false);
      return;
    }
    if (illustrationStatus === 'generating') {
      setIllustrationStuck(false);
      setIllustrationSlow(false);
      pollCountRef.current = 0;
      const maxPolls = mountedWithGenerating.current ? 5 : 75; // 20s vs 300s (5 min covers photo+face-swap worst case)
      pollRef.current = setInterval(async () => {
        pollCountRef.current += 1;
        // After 30 polls (120s) on a fresh story, show "taking longer" message
        if (!mountedWithGenerating.current && pollCountRef.current > 30) {
          setIllustrationSlow(true);
        }
        if (pollCountRef.current > maxPolls) {
          clearInterval(pollRef.current);
          setIllustrationStuck(true);
          return;
        }
        try {
          const res = await API.get(`/stories/${id}/illustration`);
          setIllustrationStatus(res.data.illustrationStatus);
          if (res.data.illustrationUrl) setIllustration(res.data.illustrationUrl);
          if (res.data.illustrationStatus !== 'generating') clearInterval(pollRef.current);
        } catch { clearInterval(pollRef.current); }
      }, 4000);
    }
    return () => clearInterval(pollRef.current);
  }, [illustrationStatus, id]);

  const handleRegenerate = async () => {
    setRegenLoading(true);
    try {
      await API.post(`/stories/${id}/illustration/regenerate`, { style: illustrationStyle });
      trackIllustrationRegenerated();
      mountedWithGenerating.current = false;
      setIllustrationStuck(false);
      setIllustrationStatus('generating');
      setIllustration(null);
    } catch (err) {
      await alert({ title: 'Error', message: err.response?.data?.message || 'Failed to regenerate illustration.', danger: true });
    } finally {
      setRegenLoading(false);
    }
  };

  const handleDownloadIllustration = async () => {
    if (!illustration) return;
    try {
      const res = await fetch(illustration);
      const blob = await res.blob();
      const filename = `${story?.title?.replace(/[^a-z0-9 ]/gi, '').replace(/\s+/g, '_') || 'illustration'}.jpg`;

      // On Android Capacitor: use native Filesystem + Share for reliable save-to-gallery
      if (isNative()) {
        try {
          const { Filesystem, Directory } = await import('@capacitor/filesystem');
          const { Share } = await import('@capacitor/share');
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
          const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
          await Share.share({ title: story?.title, files: [uri], dialogTitle: 'Save Illustration' });
          trackIllustrationDownloaded();
          recordActivity('illustration.downloaded', { storyId: id });
          return;
        } catch (err) {
          if (err?.message?.includes('cancel') || err?.message?.includes('abort')) return;
          // fall through to web approach
        }
      }

      // Web: try Web Share API with file, then anchor fallback
      if (navigator.share) {
        const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
        const shareData = { files: [file], title: story?.title };
        if (navigator.canShare && navigator.canShare(shareData)) {
          try {
            await navigator.share(shareData);
            trackIllustrationDownloaded();
            recordActivity('illustration.downloaded', { storyId: id });
            return;
          } catch (err) {
            if (err?.name === 'AbortError') return;
          }
        }
      }

      // Desktop fallback
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      trackIllustrationDownloaded();
      recordActivity('illustration.downloaded', { storyId: id });
    } catch (err) {
      console.error('Save illustration failed:', err);
    }
  };

  // ── Cloned-voice narration ─────────────────────────────────────────────
  const handleCloneNarration = async () => {
    // Pause / resume if audio already loaded
    if (cloneAudioObjRef.current && (clonePlaying || clonePaused)) {
      if (clonePaused) {
        cloneAudioObjRef.current.playbackRate = playbackSpeed;
        cloneAudioObjRef.current.play();
        setClonePaused(false);
      } else {
        cloneAudioObjRef.current.pause();
        setClonePaused(true);
      }
      return;
    }

    // Stop smart narration if playing
    if (aiPlaying && audioObjRef.current) {
      audioObjRef.current.pause();
      setAiPlaying(false); setAiPaused(false);
      clearInterval(aiSaveTimerRef.current);
    }

    // Unlock audio SYNCHRONOUSLY within the user gesture tap — before any awaits.
    const audio = new Audio(SILENT_MP3);
    audio.play().catch(() => {});
    cloneAudioObjRef.current = audio;

    setCloneLoading(true);
    try {
      const res = await API.get(`/voice/narrate/${id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      trackNarrationPlayed('clone');
      const paragraphs = (story?.content || '').split('\n\n');
      sentenceTimingsRef.current = { paragraphs, titleLen: story?.title?.length || 0, moralLen: story?.moralLesson?.length || 0 };
      audio.pause();
      audio.src = url;
      audio.playbackRate = playbackSpeed;
      audio.onloadedmetadata = () => {
        setNarrationDuration(audio.duration);
        if (resumePositionRef.current > 1 && resumePositionRef.current < audio.duration - 2) {
          audio.currentTime = resumePositionRef.current;
        }
      };
      audio.ontimeupdate = () => {
        setNarrationCurrentTime(audio.currentTime);
        const { paragraphs: paras, titleLen, moralLen } = sentenceTimingsRef.current;
        setActiveSentenceIdx(getActiveParagraphIdx(audio.currentTime, audio.duration, paras, titleLen, moralLen));
      };
      audio.onended = () => {
        setClonePlaying(false); setClonePaused(false);
        setActiveSentenceIdx(-1); setNarrationCurrentTime(0); setNarrationDuration(0);
        clearInterval(cloneSaveTimerRef.current);
        resumePositionRef.current = 0;
        API.put(`/stories/${id}/narration-position`, { position: 0 }).catch(() => {});
        recordActivity('narration.played', { storyId: id, type: 'clone', percent: 100 });
        maxNarrationPctRef.current = 0; // full listen recorded — don't re-send on unmount
      };
      audio.onerror = () => {
        setClonePlaying(false); setClonePaused(false);
        clearInterval(cloneSaveTimerRef.current);
      };
      audio.load();
      audio.play().then(() => {
        setClonePlaying(true); setClonePaused(false);
        cloneSaveTimerRef.current = setInterval(() => {
          if (cloneAudioObjRef.current && !cloneAudioObjRef.current.paused) {
            const pos = Math.floor(cloneAudioObjRef.current.currentTime);
            resumePositionRef.current = pos;
            API.put(`/stories/${id}/narration-position`, { position: pos }).catch(() => {});
          }
        }, 10000);
      }).catch(() => { setClonePlaying(false); });
    } catch (err) {
      cloneAudioObjRef.current = null;
      // responseType:'blob' means error bodies come back as Blob — parse to JSON first
      let errData = err.response?.data;
      if (errData instanceof Blob) {
        try { errData = JSON.parse(await errData.text()); } catch (_) {}
      }
      if (errData?.limitReached) {
        setTopUpType('cloneNarrations');
        setShowTopUp(true);
      } else if (errData?.unsupportedLanguage) {
        await alert({ title: 'Language not supported', message: errData.message });
      } else {
        await alert({ title: 'Narration failed', message: errData?.message || 'Failed to generate narration with your voice.', danger: true });
      }
    } finally {
      setCloneLoading(false);
    }
  };

  const handleCloneStop = () => {
    if (cloneAudioObjRef.current) {
      const pos = Math.floor(cloneAudioObjRef.current.currentTime || 0);
      if (pos > 1) {
        resumePositionRef.current = pos;
        API.put(`/stories/${id}/narration-position`, { position: pos }).catch(() => {});
      }
      cloneAudioObjRef.current.pause();
      cloneAudioObjRef.current.ontimeupdate = null;
      cloneAudioObjRef.current.onloadedmetadata = null;
      cloneAudioObjRef.current.onended = null;
      cloneAudioObjRef.current.onerror = null;
      cloneAudioObjRef.current = null;
    }
    clearInterval(cloneSaveTimerRef.current);
    setClonePlaying(false); setClonePaused(false);
    setActiveSentenceIdx(-1); setNarrationCurrentTime(0); setNarrationDuration(0);
  };

  const changePlaybackSpeed = (speed) => {
    setPlaybackSpeed(speed);
    if (audioObjRef.current) audioObjRef.current.playbackRate = speed;
    if (cloneAudioObjRef.current) cloneAudioObjRef.current.playbackRate = speed;
    // Speech synthesis doesn't support live rate changes — restart is needed,
    // but we apply it on the next utterance via playbackSpeed state.
  };

  const toggleFavorite = async () => {
    const prev = story?.isFavorite;
    setStory(s => ({ ...s, isFavorite: !prev }));
    try {
      const res = await API.put(`/stories/${id}/favorite`);
      setStory(s => ({ ...s, isFavorite: res.data.isFavorite }));
      if (res.data.isFavorite) trackStoryFavorited();
    } catch {
      setStory(s => ({ ...s, isFavorite: prev }));
    }
  };

  const handleRating = async (rating) => {
    if (ratingSubmitting) return;
    setRatingSubmitting(true);
    setStoryRating(rating);
    try {
      await API.put(`/stories/${id}/feedback`, { rating });
      // Show bedtime card after a brief delay so the rating animation completes
      setTimeout(() => setShowBedtimeCard(true), 800);
    } catch {
      // revert on error
      setStoryRating(null);
    } finally {
      setRatingSubmitting(false);
    }
  };

  // Map app language codes → BCP-47 tags used by the Web Speech API
  const SPEECH_LANG = {
    en: 'en-US', es: 'es-ES', fr: 'fr-FR', hi: 'hi-IN',
    gu: 'gu-IN', de: 'de-DE', pt: 'pt-BR', ar: 'ar-SA',
    zh: 'zh-CN', ja: 'ja-JP', it: 'it-IT',
  };

  // Browser speech synthesis (free plan) — with pause/resume + sentence highlighting
  // ── Native Android TTS (Capacitor) ────────────────────────────────────────────
  // Builds paragraphs from story for sentence-by-sentence native TTS
  const buildTtsParagraphs = (storyObj) => {
    const parts = [];
    if (storyObj.title) parts.push(storyObj.title + '.');
    const paras = (storyObj.content || '').split('\n\n').filter(p => p.trim());
    paras.forEach(p => parts.push(p.replace(/\*\*|__|\*|_/g, '').replace(/\n/g, ' ').trim()));
    if (storyObj.moralLesson) parts.push(`Tonight's lesson: ${storyObj.moralLesson}`);
    return parts.filter(Boolean);
  };

  const speakNativeParagraphsFrom = async (TextToSpeech, bcp47, startIdx) => {
    const paragraphs = buildTtsParagraphs(story);
    for (let i = startIdx; i < paragraphs.length; i++) {
      if (nativeTtsPausedRef.current || nativeTtsStoppedRef.current) {
        nativeTtsParagraphRef.current = i;
        if (nativeTtsStoppedRef.current) {
          setSpeaking(false); setSpeechPaused(false); setActiveSentenceIdx(-1);
          nativeTtsParagraphRef.current = 0;
        }
        return;
      }
      setActiveSentenceIdx(i);
      try {
        await TextToSpeech.speak({
          text: paragraphs[i],
          lang: bcp47,
          rate: playbackSpeed * 0.85,
          pitch: 1.05,
          volume: 1.0,
          category: 'ambient',
        });
      } catch {
        // speak() rejects when stop() is called mid-utterance — treat as pause/stop
        if (!nativeTtsPausedRef.current) {
          nativeTtsStoppedRef.current = true;
        }
        nativeTtsParagraphRef.current = i;
        if (nativeTtsStoppedRef.current) {
          setSpeaking(false); setSpeechPaused(false); setActiveSentenceIdx(-1);
          nativeTtsParagraphRef.current = 0;
        }
        return;
      }
    }
    // Finished all paragraphs naturally
    setSpeaking(false); setSpeechPaused(false); setActiveSentenceIdx(-1);
    nativeTtsParagraphRef.current = 0;
  };

  const handleReadAloudNative = async () => {
    const { TextToSpeech } = await import('@capacitor-community/text-to-speech');
    const langCode = story.language || 'en';
    const bcp47 = SPEECH_LANG[langCode] || langCode;

    // Pause
    if (speaking && !speechPaused) {
      nativeTtsPausedRef.current = true;
      try { await TextToSpeech.stop(); } catch {}
      setSpeechPaused(true);
      return;
    }
    // Resume
    if (speaking && speechPaused) {
      nativeTtsPausedRef.current = false;
      setSpeechPaused(false);
      await speakNativeParagraphsFrom(TextToSpeech, bcp47, nativeTtsParagraphRef.current);
      return;
    }
    // Fresh start
    nativeTtsParagraphRef.current = 0;
    nativeTtsPausedRef.current = false;
    nativeTtsStoppedRef.current = false;
    setSpeaking(true); setSpeechPaused(false);
    trackReadAloud(story.language || 'en');
    await speakNativeParagraphsFrom(TextToSpeech, bcp47, 0);
  };

  // Async voice loader — Android Chrome loads voices asynchronously via voiceschanged event
  const getVoicesAsync = () => new Promise(resolve => {
    if (!window.speechSynthesis) { resolve([]); return; }
    const immediate = window.speechSynthesis.getVoices();
    if (immediate.length) { resolve(immediate); return; }
    let done = false;
    const handler = () => {
      if (done) return;
      done = true;
      window.speechSynthesis.removeEventListener('voiceschanged', handler);
      resolve(window.speechSynthesis.getVoices());
    };
    window.speechSynthesis.addEventListener('voiceschanged', handler);
    // 3 s timeout so we never hang on browsers that never fire voiceschanged
    setTimeout(() => {
      if (done) return;
      done = true;
      window.speechSynthesis.removeEventListener('voiceschanged', handler);
      resolve(window.speechSynthesis.getVoices());
    }, 3000);
  });

  const handleReadAloud = async () => {
    // On Android Capacitor: delegate to native TTS plugin
    if (isNative()) {
      await handleReadAloudNative();
      return;
    }

    if (!window.speechSynthesis) {
      setSpeechError('Read Aloud is not available in this browser. Use the Smart Narration button to hear the story with an AI voice.');
      return;
    }

    // ── Pause ─────────────────────────────────────────────────────────────────
    if (speaking && !speechPaused) {
      if (speechResumeIntervalRef.current) { clearInterval(speechResumeIntervalRef.current); speechResumeIntervalRef.current = null; }
      if (isIOSSpeech()) {
        window.speechSynthesis.cancel(); // iOS: pause() is broken — cancel and track position
      } else {
        window.speechSynthesis.pause();
      }
      setSpeechPaused(true);
      return;
    }

    // ── Non-iOS resume (native browser API) ────────────────────────────────────
    if (speaking && speechPaused && !isIOSSpeech()) {
      window.speechSynthesis.resume();
      setSpeechPaused(false);
      speechResumeIntervalRef.current = setInterval(() => {
        if (window.speechSynthesis.paused) window.speechSynthesis.resume();
      }, 10000);
      return;
    }

    // ── Fresh start OR iOS resume — both create a new utterance ───────────────
    const langCode = story.language || 'en';
    const bcp47    = SPEECH_LANG[langCode] || langCode;
    const langPrefix = bcp47.split('-')[0];

    // Wait for voices to load (Android Chrome returns [] synchronously, fires voiceschanged async)
    setSpeechLoading(true);
    const voices = await getVoicesAsync();
    setSpeechLoading(false);
    const lesson = story.moralLesson ? ` Tonight's lesson: ${story.moralLesson}` : '';
    const fullText = (story.title + '. ' + story.content + lesson)
      .replace(/\*\*|__|\*|_/g, '')
      .replace(/\n\n+/g, '. ')
      .replace(/\n/g, ' ')
      .replace(/\.\.+/g, '.')
      .replace(/\s{2,}/g, ' ')
      .replace(/\s+([.,!?])/g, '$1')
      .trim();

    const LANG_NAMES_LOCAL = { en:'English', es:'Spanish', fr:'French', hi:'Hindi', gu:'Gujarati', de:'German', pt:'Portuguese', ar:'Arabic', zh:'Chinese', ja:'Japanese', it:'Italian' };
    const voice = voices.length
      ? (voices.find(v => v.lang === bcp47 && v.localService) ||
         voices.find(v => v.lang === bcp47) ||
         voices.find(v => v.lang.startsWith(langPrefix)) ||
         (langCode === 'en' ? voices.find(v => v.lang.startsWith('en')) : null))
      : null;

    if (!voice && langCode !== 'en') {
      const langName = LANG_NAMES_LOCAL[langCode] || langCode.toUpperCase();
      setSpeechError(`Your browser has no ${langName} voice installed. Try Smart Narration for a better experience, or read on a mobile device.`);
      return;
    }
    setSpeechError(null);

    const isFresh = !(speaking && speechPaused); // false only on iOS resume path
    const offset = isFresh ? 0 : speechLastCharRef.current;
    const text = offset > 0 && speechFullTextRef.current ? speechFullTextRef.current.slice(offset) : fullText;

    if (isFresh) {
      speechFullTextRef.current = fullText;
      speechLastCharRef.current = 0;
      speechTextOffsetRef.current = 0;
      // Build paragraph start positions for boundary-based highlighting
      const paragraphs = (story?.content || '').split('\n\n');
      const titlePart = story.title + '. ';
      let pos = titlePart.length;
      speechSentencePosRef.current = paragraphs.map(p => {
        const start = pos;
        pos += p.replace(/\*\*|__|\*|_/g, '').length + 2;
        return start;
      });
      trackReadAloud(langCode);
    }
    speechTextOffsetRef.current = offset;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang  = bcp47;
    utterance.rate  = playbackSpeed * 0.85;
    utterance.pitch = 1.05;
    if (voice) utterance.voice = voice;

    utterance.onboundary = (e) => {
      if (e.name !== 'word' && e.name !== 'sentence') return;
      const abs = e.charIndex + speechTextOffsetRef.current;
      speechLastCharRef.current = abs; // track position for iOS pause
      const posList = speechSentencePosRef.current;
      let active = -1;
      for (let i = 0; i < posList.length; i++) { if (abs >= posList[i]) active = i; else break; }
      setActiveSentenceIdx(active);
    };
    utterance.onend = () => {
      if (speechResumeIntervalRef.current) { clearInterval(speechResumeIntervalRef.current); speechResumeIntervalRef.current = null; }
      setSpeaking(false); setSpeechPaused(false); setActiveSentenceIdx(-1);
      speechLastCharRef.current = 0; speechTextOffsetRef.current = 0;
    };
    utterance.onerror = (e) => {
      if (e.error !== 'interrupted') {
        if (speechResumeIntervalRef.current) { clearInterval(speechResumeIntervalRef.current); speechResumeIntervalRef.current = null; }
        setSpeaking(false); setSpeechPaused(false); setActiveSentenceIdx(-1);
      }
    };
    utteranceRef.current = utterance;
    // Do NOT call cancel() before speak() — on Android Chrome it silently cancels the new utterance
    window.speechSynthesis.speak(utterance);
    // iOS Safari pauses speech after ~15s — keep it alive with periodic resume
    speechResumeIntervalRef.current = setInterval(() => {
      if (window.speechSynthesis.paused && !speechPaused) window.speechSynthesis.resume();
    }, 10000);
    setSpeaking(true); setSpeechPaused(false);
  };

  const handleReadAloudStop = () => {
    if (isNative()) {
      nativeTtsStoppedRef.current = true;
      nativeTtsPausedRef.current = false;
      nativeTtsParagraphRef.current = 0;
      import('@capacitor-community/text-to-speech').then(({ TextToSpeech }) => TextToSpeech.stop()).catch(() => {});
    }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    if (speechResumeIntervalRef.current) { clearInterval(speechResumeIntervalRef.current); speechResumeIntervalRef.current = null; }
    setSpeaking(false); setSpeechPaused(false); setActiveSentenceIdx(-1);
    speechLastCharRef.current = 0; speechTextOffsetRef.current = 0;
  };

  // AI narration (basic/family plan)
  const handleAiNarration = async () => {
    // Pause / resume if audio already loaded
    if (audioObjRef.current && (aiPlaying || aiPaused)) {
      if (aiPaused) {
        audioObjRef.current.playbackRate = playbackSpeed;
        audioObjRef.current.play();
        setAiPaused(false);
      } else {
        audioObjRef.current.pause();
        setAiPaused(true);
      }
      return;
    }

    // Stop clone narration if playing
    if (clonePlaying && cloneAudioObjRef.current) {
      cloneAudioObjRef.current.pause();
      setClonePlaying(false); setClonePaused(false);
      clearInterval(cloneSaveTimerRef.current);
    }

    // Unlock audio SYNCHRONOUSLY within the user gesture tap — before any awaits.
    const audio = new Audio(SILENT_MP3);
    audio.play().catch(() => {});
    audioObjRef.current = audio;

    setAiLoading(true);
    try {
      const res = await API.get(`/stories/${id}/narrate`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      trackNarrationPlayed('ai');
      const paragraphs = (story?.content || '').split('\n\n');
      sentenceTimingsRef.current = { paragraphs, titleLen: story?.title?.length || 0, moralLen: story?.moralLesson?.length || 0 };
      audio.pause();
      audio.src = url;
      audio.playbackRate = playbackSpeed;
      audio.onloadedmetadata = () => {
        setNarrationDuration(audio.duration);
        if (resumePositionRef.current > 1 && resumePositionRef.current < audio.duration - 2) {
          audio.currentTime = resumePositionRef.current;
        }
      };
      audio.ontimeupdate = () => {
        setNarrationCurrentTime(audio.currentTime);
        const { paragraphs: paras, titleLen, moralLen } = sentenceTimingsRef.current;
        setActiveSentenceIdx(getActiveParagraphIdx(audio.currentTime, audio.duration, paras, titleLen, moralLen));
      };
      audio.onended = () => {
        setAiPlaying(false); setAiPaused(false);
        setActiveSentenceIdx(-1); setNarrationCurrentTime(0); setNarrationDuration(0);
        clearInterval(aiSaveTimerRef.current);
        resumePositionRef.current = 0;
        API.put(`/stories/${id}/narration-position`, { position: 0 }).catch(() => {});
        recordActivity('narration.played', { storyId: id, type: 'ai', percent: 100 });
        maxNarrationPctRef.current = 0; // full listen recorded — don't re-send on unmount
      };
      audio.onerror = () => {
        setAiPlaying(false); setAiPaused(false);
        clearInterval(aiSaveTimerRef.current);
      };
      audio.load();
      audio.play().then(() => {
        setAiPlaying(true); setAiPaused(false);
        aiSaveTimerRef.current = setInterval(() => {
          if (audioObjRef.current && !audioObjRef.current.paused) {
            const pos = Math.floor(audioObjRef.current.currentTime);
            resumePositionRef.current = pos;
            API.put(`/stories/${id}/narration-position`, { position: pos }).catch(() => {});
          }
        }, 10000);
      }).catch(() => setAiPlaying(false));
    } catch (err) {
      audioObjRef.current = null;
      let errData = err.response?.data;
      if (errData instanceof Blob) {
        try { errData = JSON.parse(await errData.text()); } catch (_) {}
      }
      if (errData?.limitReached) {
        setTopUpType('narrations');
        setShowTopUp(true);
      } else if (errData?.unsupportedLanguage) {
        await alert({ title: 'Language not supported', message: errData.message });
      } else {
        await alert({ title: 'Narration failed', message: errData?.message || 'Failed to generate narration.', danger: true });
      }
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiStop = () => {
    if (audioObjRef.current) {
      const pos = Math.floor(audioObjRef.current.currentTime || 0);
      if (pos > 1) {
        resumePositionRef.current = pos;
        API.put(`/stories/${id}/narration-position`, { position: pos }).catch(() => {});
      }
      audioObjRef.current.pause();
      audioObjRef.current.ontimeupdate = null;
      audioObjRef.current.onloadedmetadata = null;
      audioObjRef.current.onended = null;
      audioObjRef.current.onerror = null;
      audioObjRef.current = null;
    }
    clearInterval(aiSaveTimerRef.current);
    setAiPlaying(false); setAiPaused(false);
    setActiveSentenceIdx(-1); setNarrationCurrentTime(0); setNarrationDuration(0);
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await API.get(`/stories/${id}/export/pdf`, { responseType: 'blob' });
      const filename = `${story.title.replace(/[^a-z0-9 ]/gi, '').replace(/\s+/g, '_') || 'story'}.pdf`;
      // Was a bare <a download> click, which the Capacitor WebView ignores: in
      // the app the spinner stopped and no file ever appeared. The library's
      // export already routed through the native share sheet; this one did not.
      const how = await saveOrShareFile(res.data, filename, { title: story.title, dialogTitle: 'Save Story PDF' });
      // The library counts its exports; this surface never did, so the metric
      // only ever saw half of them.
      if (how !== 'cancelled') trackPdfExported();
    } catch (err) {
      await alert({ title: 'Export failed', message: err.response?.data?.message || 'Failed to export PDF.', danger: true });
    } finally {
      setExporting(false);
    }
  };

  // Tomorrow's recommended story concept — shown in the completion section
  useEffect(() => {
    const childId = story?.child?._id;
    if (!childId) return;
    API.get(`/stories/tonight/${childId}?offset=1`)
      .then(res => setTomorrowIdea(res.data.tonight))
      .catch(() => {}); // section simply omits the suggestion
  }, [story?.child?._id]);

  const openShareModal = async () => {
    setShowShareModal(true);
    setShareLoading(true);
    try {
      const res = await API.get(`/stories/${id}/share`);
      if (res.data.shareLink) setShareLink(res.data.shareLink);
    } catch { /* silent */ }
    finally { setShareLoading(false); }
  };

  const handleCreateShareLink = async () => {
    setShareLoading(true);
    try {
      const payload = { expiryDays: shareForm.expiryDays || null };
      if (shareForm.password !== undefined) payload.password = shareForm.password;
      const res = await API.post(`/stories/${id}/share`, payload);
      setShareLink(res.data);
      setShareForm(f => ({ ...f, password: '' }));
      trackStoryShared('link_created');
    } catch (err) {
      await alert({ title: 'Error', message: err.response?.data?.message || 'Failed to create share link.', danger: true });
    } finally { setShareLoading(false); }
  };

  const handleRevokeShareLink = async () => {
    const ok = await confirm({ title: 'Revoke share link?', message: 'Anyone with this link will immediately lose access.', confirmLabel: 'Revoke', danger: true });
    if (!ok) return;
    try {
      await API.delete(`/stories/${id}/share`);
      setShareLink(null);
    } catch { await alert({ title: 'Error', message: 'Failed to revoke link.', danger: true }); }
  };

  const copyShareUrl = () => {
    if (!shareLink?.url) return;
    copyToClipboard(shareLink.url).then((ok) => {
      if (!ok) return;
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
      trackStoryShared('copy_link');
    });
  };

  const handleQuickShare = async () => {
    if (showQuickShare) { setShowQuickShare(false); return; }
    setShowQuickShare(true);
    if (!shareLink) {
      setShareLoading(true);
      try {
        const res = await API.post(`/stories/${id}/share`, { expiryDays: '30' });
        setShareLink(res.data);
      } catch (err) {
        console.error('[share] auto-create failed', err.message);
      } finally {
        setShareLoading(false);
      }
    }
  };

  const handleNativeShare = async () => {
    if (!shareLink?.url) return;
    try {
      await navigator.share({
        title: story?.title,
        text: `🌙 "${story?.title}" — a personalised bedtime story starring ${story?.child?.name || 'your child'} ✨`,
        url: shareLink.url,
      });
      trackStoryShared(id, 'native_share');
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Native share failed:', err);
    }
  };

  const handleGenerateQuiz = async () => {
    setQuizLoading(true);
    try {
      const res = await API.post(`/stories/${id}/quiz`);
      setQuiz(res.data.quiz);
      setQuizAnswers({});
      setQuizSubmitted(false);
    } catch (err) {
      await alert({ title: 'Quiz generation failed', message: err.response?.data?.message || 'Failed to generate quiz. Please try again.', danger: true });
    } finally {
      setQuizLoading(false);
    }
  };

  const openPublishModal = () => {
    setPublishName(story?.publishedChildName || story?.child?.name || '');
    setShowPublishModal(true);
  };

  const handleTogglePublish = async () => {
    setPublishLoading(true);
    try {
      const payload = story?.isPublished ? {} : { publishedChildName: publishName || story?.child?.name };
      const res = await API.put(`/stories/${id}/publish`, payload);
      setStory(s => ({ ...s, isPublished: res.data.isPublished, publishSlug: res.data.publishSlug, publishedChildName: res.data.publishedChildName }));
      if (res.data.isPublished) trackCommunityPublish();
      setShowPublishModal(false);
    } catch (err) {
      await alert({ title: 'Error', message: err.response?.data?.message || 'Failed to update publish status.', danger: true });
    } finally {
      setPublishLoading(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({ title: 'Delete story?', message: 'This story will be permanently deleted and cannot be recovered.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      await API.delete(`/stories/${id}`);
      trackStoryDeleted();
      navigate('/dashboard/library');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete story. Please try again.');
    }
  };

  // ── "Continue the Story" handlers ─────────────────────────────────────────────
  const pollContinuation = (idx) => {
    let polls = 0;
    if (contPollRef.current) clearInterval(contPollRef.current);
    contPollRef.current = setInterval(async () => {
      polls++;
      if (polls > 90) {
        clearInterval(contPollRef.current);
        setContGenerating(false);
        setContinuations(prev => {
          const updated = [...prev];
          if (updated[idx]) updated[idx] = { ...updated[idx], generationStatus: 'failed' };
          return updated;
        });
        return;
      }
      try {
        const res = await API.get(`/stories/${id}/continue/${idx}/status`);
        if (res.data.generationStatus === 'ready') {
          clearInterval(contPollRef.current);
          setContGenerating(false);
          setContinuations(prev => {
            const updated = [...prev];
            updated[idx] = res.data.continuation;
            return updated;
          });
        } else if (res.data.generationStatus === 'failed') {
          clearInterval(contPollRef.current);
          setContGenerating(false);
          setContinuations(prev => {
            const updated = [...prev];
            if (updated[idx]) updated[idx] = { ...updated[idx], generationStatus: 'failed' };
            return updated;
          });
        }
      } catch { /* keep polling on transient network errors */ }
    }, 3000);
  };

  const handleContinueStory = async () => {
    setContError(null);
    try {
      const res = await API.post(`/stories/${id}/continue`);
      const idx = res.data.contIndex;
      setContinuations(prev => {
        const updated = [...prev];
        updated[idx] = { generationStatus: 'pending', title: '', content: '' };
        return updated;
      });
      setContGenerating(true);
      pollContinuation(idx);
    } catch (err) {
      const msg = err.response?.data?.message || 'Something went wrong.';
      if (err.response?.data?.limitReached) {
        setShowTopUp(true);
        setTopUpType('stories');
      } else {
        setContError(msg);
      }
    }
  };

  // ── Generating overlay — shown while background worker is running ────────────
  if (isGenerating && (loading || genFailed)) return (
    <DashboardLayout>
      {genFailed ? (
        <div className="text-center py-5">
          <div style={{fontSize:'48px', marginBottom:'12px'}}>😔</div>
          <h5 className="fw-bold mb-2" style={{color:'var(--text-main)'}}>Story generation failed</h5>
          <p className="text-muted small mb-4">Something went wrong on our end. Your story credit has been refunded — please try again.</p>
          <button className="btn btn-dream rounded-pill px-4" onClick={() => navigate('/dashboard/generate')}>
            Try again
          </button>
        </div>
      ) : (
        <div className="text-center py-5">
          <div className="mb-4" style={{fontSize:'64px', lineHeight:1}}>🌙</div>
          {/* Pulsing ring */}
          <div className="position-relative d-inline-block mb-4">
            <div style={{
              width: '80px', height: '80px', borderRadius: '50%',
              border: '4px solid #e8d5ff',
              borderTopColor: '#6f42c1',
              animation: 'spin 1s linear infinite',
              display: 'inline-block',
            }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
          <h5 className="fw-bold mb-2" style={{color:'var(--text-main)', fontFamily:"'Fredoka One',cursive"}}>
            Crafting your story…
          </h5>
          <p className="text-muted mb-1" style={{fontSize:'14px'}}>{genMsg}</p>
          <p className="text-muted" style={{fontSize:'12px'}}>This usually takes 15–30 seconds</p>
        </div>
      )}
    </DashboardLayout>
  );

  if (loading) return (
    <DashboardLayout>
      <div className="text-center py-5"><div className="spinner-border" style={{color:'#6f42c1'}}></div></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      {showReveal && <StoryRevealAnimation onComplete={() => setShowReveal(false)} />}
      {showBedtimeCard && story && (
        <BedtimeCard
          story={story}
          childName={story.child?.name}
          streak={0}
          onClose={() => setShowBedtimeCard(false)}
          reflectionPrompts={null}
        />
      )}
      {showGrandparentModal && story && (
        <GrandparentShareModal
          storyId={story._id}
          storyTitle={story.title}
          onClose={() => setShowGrandparentModal(false)}
        />
      )}

      {showConfetti && <ConfettiBurst />}

      {/* First-story celebration banner */}
      {isFirstStory && (
        <div className="mb-4 p-4 text-center rounded-4" style={{
          background: 'linear-gradient(135deg,#6f42c1,#9b6fe8)',
          color: 'white',
        }}>
          <div style={{fontSize:'40px', marginBottom:'8px'}}>🎉</div>
          <h4 className="fw-bold mb-1" style={{fontFamily:"'Fredoka One',cursive"}}>
            {story?.child?.name ? `${story.child.name}'s first story is ready!` : 'Your first story is ready!'}
          </h4>
          <p className="mb-3" style={{opacity:0.9, fontSize:'14px'}}>
            It's been saved to your library forever. Read it together tonight — tap the speaker to hear it read aloud.
          </p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <div className="px-3 py-2 rounded-pill" style={{background:'rgba(255,255,255,0.15)', fontSize:'13px'}}>
              📚 Saved to your library
            </div>
            <div className="px-3 py-2 rounded-pill" style={{background:'rgba(255,255,255,0.15)', fontSize:'13px'}}>
              🔊 Read-aloud ready
            </div>
            <div className="px-3 py-2 rounded-pill" style={{background:'rgba(255,255,255,0.15)', fontSize:'13px'}}>
              🎨 Illustration generating
            </div>
          </div>
        </div>
      )}

      {/* ── WhatsApp / share nudge (shown on every new story, once) ── */}
      {isNew && !sharePromptDismissed && story && (
        <div className="mb-4 rounded-4 p-3 p-md-4 d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-3"
          style={{background:'#f0fdf4', border:'1.5px solid #86efac', position:'relative'}}>
          <button
            onClick={() => setSharePromptDismissed(true)}
            style={{position:'absolute', top:'10px', right:'14px', background:'none', border:'none', fontSize:'18px', color:'#6b7280', cursor:'pointer', lineHeight:1}}
            aria-label="Dismiss"
          >×</button>
          <div style={{fontSize:'32px', flexShrink:0}}>📲</div>
          <div className="flex-grow-1">
            <div className="fw-bold mb-1" style={{color:'#15803d', fontSize:'15px'}}>
              Know a parent who'd love this?
            </div>
            <div style={{color:'#4b5563', fontSize:'13px', lineHeight:1.5}}>
              Share "{story.title}" — send the link to a family WhatsApp group or a friend.
            </div>
          </div>
          <div className="d-flex gap-2 flex-shrink-0 flex-wrap">
            {/* WhatsApp */}
            <a
              href={shareLink?.url
                ? `https://wa.me/?text=${encodeURIComponent(`🌙 "${story.title}" — a personalised bedtime story starring ${story?.child?.name || 'my child'} ✨\n\nRead it here (no account needed): ${shareLink.url}`)}`
                : `https://wa.me/?text=${encodeURIComponent(`🌙 I just made a personalised bedtime story for ${story?.child?.name || 'my child'} on Dreamy Bedtime! ✨\n\nYour child can star in their own story too → https://dreamybedtime.com`)}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm fw-semibold rounded-pill px-3"
              style={{background:'#25d366', color:'#fff', fontSize:'13px'}}
              onClick={() => trackStoryShared(id, 'whatsapp_nudge')}
            >
              <i className="bi bi-whatsapp me-1"></i>WhatsApp
            </a>
            {/* iMessage / SMS */}
            <a
              href={shareLink?.url
                ? `sms:?body=${encodeURIComponent(`🌙 "${story.title}" starring ${story?.child?.name || 'my child'} ✨ Read it here: ${shareLink.url}`)}`
                : `sms:?body=${encodeURIComponent(`🌙 I made a personalised bedtime story on Dreamy Bedtime! Your child can have their own too → https://dreamybedtime.com`)}`
              }
              className="btn btn-sm fw-semibold rounded-pill px-3"
              style={{background:'#3b82f6', color:'#fff', fontSize:'13px'}}
              onClick={() => trackStoryShared(id, 'imessage_nudge')}
            >
              <i className="bi bi-chat-fill me-1"></i>iMessage
            </a>
            {/* Copy link */}
            <button
              className="btn btn-sm btn-outline-secondary rounded-pill px-3"
              style={{fontSize:'13px'}}
              onClick={() => {
                copyToClipboard(shareLink?.url || 'https://dreamybedtime.com').then((ok) => {
                  if (!ok) return;
                  setSharePromptCopied(true);
                  setTimeout(() => setSharePromptCopied(false), 2000);
                  trackStoryShared(id, 'copy_nudge');
                });
              }}
            >
              {sharePromptCopied
                ? <><i className="bi bi-check2 me-1"></i>Copied!</>
                : <><i className="bi bi-link-45deg me-1"></i>Copy link</>
              }
            </button>
            {/* Story card — shareable image for Instagram / social */}
            <button
              className="btn btn-sm rounded-pill px-3 fw-semibold"
              style={{background:'linear-gradient(135deg,#6f42c1,#9b59b6)', color:'#fff', border:'none', fontSize:'13px'}}
              onClick={() => { setShowCardModal(true); trackStoryShared(id, 'card_nudge'); }}
            >
              <i className="bi bi-image me-1"></i>Story Card
            </button>
          </div>
        </div>
      )}

      {/* Regular new-story banner (not first story) */}
      {isNew && !isFirstStory && (
        <div className="alert border-0 mb-4 d-flex align-items-center gap-3" style={{background:'#d4edda', borderRadius:'12px'}}>
          <span className="fs-3">🎉</span>
          <div>
            <strong>Your story is ready!</strong> Tonight's bedtime story has been created and saved to your library.
          </div>
        </div>
      )}

      <div className="row justify-content-center">
        <div className="col-lg-8">
          {/* Controls bar */}
          <div className="card border-0 shadow-sm mb-4 p-3" style={{borderRadius:'16px', background: darkMode ? '#1a1438' : undefined}}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <Link to="/dashboard/library" className="btn btn-sm btn-outline-secondary rounded-pill">
                  <i className="bi bi-arrow-left me-1"></i>Library
                </Link>
                <span className="text-muted small">
                  {story?.isMultiChild && story?.multiChildIds?.length > 0
                    ? <>{story.multiChildIds.map(c => c.avatar).join('')} {story.multiChildIds.map(c => c.name).join(' & ')}</>
                    : <>{story?.child?.avatar} {story?.child?.name}</>
                  } · {story?.readingTime || Math.max(1, Math.round((story?.content?.trim().split(/\s+/).filter(Boolean).length || 650) / 130))} min read
                </span>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <button className="btn btn-sm btn-outline-secondary rounded-circle" onClick={() => setFontSize(f => Math.max(14, f-2))} title="Smaller text">A-</button>
                <button className="btn btn-sm btn-outline-secondary rounded-circle" onClick={() => setFontSize(f => Math.min(28, f+2))} title="Larger text">A+</button>

                {/* Browser read-aloud — all plans, always shown; waits for Android async voice load */}
                <>
                  <button
                    className={`btn btn-sm rounded-pill px-3 ${speaking && !speechPaused ? 'btn-warning' : speaking && speechPaused ? 'btn-outline-warning' : 'btn-outline-secondary'}`}
                    onClick={handleReadAloud}
                    disabled={speechLoading}
                  >
                    {speechLoading
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Loading…</>
                      : <><i className={`bi ${speaking && !speechPaused ? 'bi-pause-fill' : 'bi-play-fill'} me-1`}></i>
                          {speaking && !speechPaused ? 'Pause' : speaking && speechPaused ? 'Resume' : 'Read Aloud'}</>
                    }
                  </button>
                  {speaking && (
                    <button className="btn btn-sm btn-danger rounded-pill px-3" onClick={handleReadAloudStop}>
                      <i className="bi bi-stop-fill me-1"></i>Stop
                    </button>
                  )}
                </>

                {speechError && (
                  <div className="d-flex align-items-center gap-1 px-3 py-1 rounded-pill" style={{ background: '#fff8ed', border: '1px solid #fcd34d', fontSize: '11px', color: '#92400e', maxWidth: '340px' }}>
                    <i className="bi bi-info-circle-fill" style={{ flexShrink: 0 }}></i>
                    <span>{speechError}</span>
                    <button onClick={() => setSpeechError(null)} style={{ background: 'none', border: 'none', padding: 0, color: '#92400e', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}>×</button>
                  </div>
                )}

                {hasPaidPlan && (
                  // AI Narration for Basic/Family — play / pause / stop
                  <>
                    <button
                      className={`btn btn-sm rounded-pill px-3 ${aiPlaying && !aiPaused ? 'btn-warning' : aiPaused ? 'btn-outline-warning' : 'btn-dream'}`}
                      onClick={remaining.narrations <= 0 && !aiPlaying && !aiPaused ? () => { setTopUpType('narrations'); setShowTopUp(true); } : handleAiNarration}
                      disabled={aiLoading}
                      title={remaining.narrations <= 0 && !aiPlaying && !aiPaused ? 'Narration limit reached — buy more' : 'High-quality smart narration'}
                    >
                      {aiLoading
                        ? <><span className="spinner-border spinner-border-sm me-1"></span>Generating...</>
                        : aiPlaying && !aiPaused
                          ? <><i className="bi bi-pause-fill me-1"></i>Pause</>
                          : aiPaused
                            ? <><i className="bi bi-play-fill me-1"></i>Resume</>
                            : <><i className="bi bi-stars me-1"></i>Smart Narration</>
                      }
                    </button>
                    {(aiPlaying || aiPaused) && (
                      <button className="btn btn-sm btn-danger rounded-pill px-3" onClick={handleAiStop}>
                        <i className="bi bi-stop-fill me-1"></i>Stop
                      </button>
                    )}
                  </>
                )}

                {/* My Voice narration — Family plan with a saved voice profile */}
                {flags.voiceCloning && isFamily && voiceProfile?.status === 'ready' && (
                  <>
                    <button
                      className={`btn btn-sm rounded-pill px-3 fw-semibold ${clonePlaying && !clonePaused ? 'btn-warning' : clonePaused ? 'btn-outline-warning' : ''}`}
                      style={!clonePlaying && !clonePaused ? {background: remaining.cloneNarrations <= 0 ? '#9ca3af' : 'linear-gradient(135deg,#f97316,#fb923c)', color:'white', border:'none'} : {}}
                      onClick={remaining.cloneNarrations <= 0 && !clonePlaying && !clonePaused ? () => { setTopUpType('cloneNarrations'); setShowTopUp(true); } : handleCloneNarration}
                      disabled={cloneLoading}
                      title={remaining.cloneNarrations <= 0 && !clonePlaying && !clonePaused ? 'Voice limit reached — buy more' : `Narrate in ${voiceProfile.name}`}
                    >
                      {cloneLoading
                        ? <><span className="spinner-border spinner-border-sm me-1"></span>Generating…</>
                        : clonePlaying && !clonePaused
                          ? <><i className="bi bi-pause-fill me-1"></i>Pause</>
                          : clonePaused
                            ? <><i className="bi bi-play-fill me-1"></i>Resume</>
                            : <><i className="bi bi-mic-fill me-1"></i>My Voice</>
                      }
                    </button>
                    {(clonePlaying || clonePaused) && (
                      <button className="btn btn-sm btn-danger rounded-pill px-3" onClick={handleCloneStop}>
                        <i className="bi bi-stop-fill me-1"></i>Stop
                      </button>
                    )}
                  </>
                )}

                <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" onClick={() => setShowCardModal(true)}
                  title="Generate a shareable image card">
                  <i className="bi bi-image me-1"></i>Share Card
                </button>

                <button className={`btn btn-sm rounded-pill px-3 ${story?.isFavorite ? 'btn-danger' : 'btn-outline-danger'}`} onClick={toggleFavorite}>
                  <i className={`bi ${story?.isFavorite ? 'bi-heart-fill' : 'bi-heart'} me-1`}></i>
                  {story?.isFavorite ? 'Saved' : 'Save'}
                </button>
                {flags.pdfExport && (hasPaidPlan ? (
                  <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" onClick={handleExportPdf} disabled={exporting}>
                    {exporting
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Exporting...</>
                      : <><i className="bi bi-file-earmark-pdf me-1"></i>Export PDF</>}
                  </button>
                ) : (
                  <Link to="/pricing" className="btn btn-sm btn-outline-secondary rounded-pill px-3" title="Upgrade to export PDF">
                    <i className="bi bi-lock-fill me-1"></i>Export PDF
                  </Link>
                ))}
                <button
                  className={`btn btn-sm rounded-pill px-3 ${showQuickShare ? 'btn-dream' : 'btn-outline-secondary'}`}
                  onClick={handleQuickShare}
                  title="Share this story"
                >
                  <i className="bi bi-share me-1"></i>Share
                </button>

                {/* Grandparent share button */}
                <button
                  className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                  onClick={() => setShowGrandparentModal(true)}
                  title="Send to grandparents"
                >
                  <i className="bi bi-envelope-heart me-1"></i>Send to family
                </button>

              </div>
            </div>
          </div>

          {/* ── Quick share panel ── */}
          {showQuickShare && (
            <div className="mb-4 p-3 rounded-4" style={{background:'var(--card-bg)', border:'1.5px solid var(--border-purple)', boxShadow:'0 4px 16px rgba(111,66,193,0.1)'}}>
              {shareLoading ? (
                <div className="text-center py-2">
                  <div className="spinner-border spinner-border-sm me-2" style={{color:'#6f42c1'}}></div>
                  <span className="text-muted small">Creating your share link…</span>
                </div>
              ) : shareLink ? (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <span className="fw-semibold small" style={{color:'var(--text-main)'}}>
                      📲 Share "{story?.title}"
                    </span>
                    <button
                      onClick={() => setShowQuickShare(false)}
                      style={{background:'none', border:'none', color:'var(--text-soft)', fontSize:'18px', lineHeight:1, cursor:'pointer', padding:'0 4px'}}
                    >×</button>
                  </div>

                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {/* WhatsApp */}
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`🌙 "${story?.title}" — a personalised bedtime story starring ${story?.child?.name || 'my child'} ✨\n\nRead it here (no account needed): ${shareLink.url}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm fw-semibold rounded-pill px-3"
                      style={{background:'#25d366', color:'#fff', fontSize:'13px'}}
                      onClick={() => trackStoryShared(id, 'whatsapp')}
                    >
                      <i className="bi bi-whatsapp me-1"></i>WhatsApp
                    </a>

                    {/* iMessage / SMS */}
                    <a
                      href={`sms:?body=${encodeURIComponent(`🌙 "${story?.title}" — a personalised bedtime story starring ${story?.child?.name || 'my child'} ✨\nRead it here: ${shareLink.url}`)}`}
                      className="btn btn-sm fw-semibold rounded-pill px-3"
                      style={{background:'#3b82f6', color:'#fff', fontSize:'13px'}}
                      onClick={() => trackStoryShared(id, 'imessage')}
                    >
                      <i className="bi bi-chat-fill me-1"></i>iMessage / SMS
                    </a>

                    {/* Email */}
                    <a
                      href={`mailto:?subject=${encodeURIComponent(`A bedtime story: ${story?.title}`)}&body=${encodeURIComponent(`Hi!\n\nI made a personalised bedtime story starring ${story?.child?.name || 'my child'} and wanted to share it with you.\n\nRead it here — no account needed:\n${shareLink.url}\n\nMade with Dreamy Bedtime ✨`)}`}
                      className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                      style={{fontSize:'13px'}}
                      onClick={() => trackStoryShared(id, 'email')}
                    >
                      <i className="bi bi-envelope me-1"></i>Email
                    </a>

                    {/* Copy link */}
                    <button
                      className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                      style={{fontSize:'13px'}}
                      onClick={() => {
                        copyToClipboard(shareLink.url).then((ok) => {
                          if (!ok) return;
                          setQuickShareCopied(true);
                          setTimeout(() => setQuickShareCopied(false), 2500);
                          trackStoryShared(id, 'copy_quick');
                        });
                      }}
                    >
                      {quickShareCopied
                        ? <><i className="bi bi-check2 me-1"></i>Copied!</>
                        : <><i className="bi bi-link-45deg me-1"></i>Copy link</>
                      }
                    </button>

                    {/* Native share sheet (iOS / Android) */}
                    {navigator.share && (
                      <button
                        className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                        style={{fontSize:'13px'}}
                        onClick={handleNativeShare}
                      >
                        <i className="bi bi-box-arrow-up me-1"></i>More
                      </button>
                    )}
                  </div>

                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <p className="text-muted mb-0" style={{fontSize:'11px'}}>
                      <i className="bi bi-people me-1"></i>
                      Anyone with this link can read — no account needed.
                      {shareLink.viewCount > 0 && <span className="ms-2">👁 {shareLink.viewCount} {shareLink.viewCount === 1 ? 'view' : 'views'}</span>}
                    </p>
                    {/* Family users: link to advanced settings */}
                    {isFamily && (
                      <button
                        className="btn btn-sm btn-link p-0 text-decoration-none"
                        style={{color:'#7c3aed', fontSize:'11px'}}
                        onClick={() => { setShowQuickShare(false); openShareModal(); }}
                      >
                        <i className="bi bi-gear me-1"></i>Advanced settings
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-2 text-muted small">
                  Could not create share link. Please try again.
                </div>
              )}
            </div>
          )}

          {/* Story content */}
          <div className="card border-0 shadow-sm" style={{borderRadius:'20px', overflow:'hidden', background: darkMode ? '#1a1438' : undefined}}>
            <div className="p-2" style={{background:'linear-gradient(135deg,#2d1b69,#6f42c1)'}}></div>
            <div className="p-4 p-lg-5">
              <div className="text-center mb-4">
                <h2 className="fw-bold mb-2" style={{color: darkMode ? '#e8d5ff' : '#2d1b69', fontFamily:"'Fredoka One',cursive", fontSize:'32px'}}>{story?.title}</h2>
                <div className="d-flex justify-content-center gap-2 flex-wrap mb-3">
                  {story?.tags?.map(tag => (
                    <span key={tag} className="badge rounded-pill small" style={{background:'var(--purple-section)', color:'#c9a8ff'}}>{tag}</span>
                  ))}
                  {story?.language && story.language !== 'en' && (
                    <span className="badge rounded-pill small" style={{background:'#fff3cd', color:'#856404'}}>
                      {LANG_FLAGS[story.language] || '🌐'} {LANG_NAMES[story.language] || story.language}
                    </span>
                  )}
                  {story?.isMultiChild && (
                    <span className="badge rounded-pill small" style={{background:'var(--purple-section)', color:'#c9a8ff'}}>
                      👨‍👩‍👧‍👦 Family Story
                    </span>
                  )}
                  {story?.learningTopic && (
                    <span className="badge rounded-pill small" style={{background:'#dcfce7', color:'#15803d'}}>
                      🎓 {story.learningTopic}
                    </span>
                  )}
                </div>

                {/* ── Illustration ── */}
                {illustrationStatus === 'generating' && !illustrationStuck && (
                  <div className="mx-auto mb-4 d-flex flex-column align-items-center justify-content-center"
                    style={{width:'100%', maxWidth:'480px', height:'300px', borderRadius:'20px',
                      background:'linear-gradient(135deg,#f0e8ff,#e8d5ff)', border:'2px dashed #c9a8ff'}}>
                    <div className="spinner-border mb-3" style={{color:'#6f42c1', width:'2.5rem', height:'2.5rem'}}></div>
                    <div className="fw-semibold" style={{color:'#6f42c1'}}>
                      {illustrationSlow ? 'Almost there, still working…' : 'Creating your illustration…'}
                    </div>
                    <div className="text-muted small mt-1">
                      {illustrationSlow ? 'Photo illustrations can take up to 2 minutes' : 'Usually takes 30–90 seconds'}
                    </div>
                  </div>
                )}
                {illustrationStuck && (
                  <div className="mx-auto mb-4 d-flex flex-column align-items-center justify-content-center"
                    style={{width:'100%', maxWidth:'480px', minHeight:'160px', borderRadius:'20px', padding:'28px 24px',
                      background:'linear-gradient(135deg,#fff8e1,#fff3cd)', border:'2px dashed #ffe082'}}>
                    <div style={{fontSize:'2rem', marginBottom:'10px'}}>⚠️</div>
                    <div className="fw-semibold mb-1" style={{color:'#7a5c00'}}>Illustration didn't generate</div>
                    <div className="text-muted small mb-3 text-center">Something went wrong while creating the illustration. You can regenerate it for free.</div>
                    <button
                      className="btn btn-sm fw-semibold"
                      style={{background:'#6f42c1', color:'#fff', borderRadius:'20px', padding:'7px 24px'}}
                      onClick={handleRegenerate}
                      disabled={regenLoading}
                    >
                      {regenLoading ? <><span className="spinner-border spinner-border-sm me-2" />Regenerating…</> : <><i className="bi bi-arrow-clockwise me-1" />Regenerate for free</>}
                    </button>
                  </div>
                )}

                {(illustrationStatus === 'done' || illustrationStatus === 'done_no_face') && illustration && (
                  <div className="mx-auto mb-4" style={{maxWidth:'480px'}}>
                    {illustrationStatus === 'done_no_face' && (
                      <div className="d-flex align-items-start gap-2 mb-2 p-2 rounded-3"
                        style={{background:'#fff8e1', border:'1px solid #ffe082', fontSize:'13px', color:'#7a5c00'}}>
                        <span style={{fontSize:'16px'}}>⚠️</span>
                        <span>We couldn't place your child's face in this illustration — no credit was charged. The scene has been generated without the photo. You can try regenerating.</span>
                      </div>
                    )}
                    <div style={{borderRadius:'20px', overflow:'hidden', boxShadow:'0 8px 32px rgba(111,66,193,0.18)', position:'relative'}}>
                      <img
                        src={illustration}
                        alt={story?.title}
                        style={{width:'100%', display:'block'}}
                      />
                      {/* Action buttons overlaid at bottom */}
                      <div className="d-flex gap-2 justify-content-center p-2"
                        style={{position:'absolute', bottom:0, left:0, right:0,
                          background:'linear-gradient(to top, rgba(45,27,105,0.85), transparent)'}}>
                        <button
                          className="btn btn-sm rounded-pill px-3 fw-semibold"
                          style={{background:'var(--card-bg)', color:'var(--text-main)', fontSize:'12px'}}
                          onClick={handleDownloadIllustration}
                          title="Download illustration"
                        >
                          <i className="bi bi-download me-1"></i>Save
                        </button>
                        {navigator.share && (
                          <button
                            className="btn btn-sm rounded-pill px-3 fw-semibold"
                            style={{background:'var(--card-bg)', color:'var(--text-main)', fontSize:'12px'}}
                            onClick={async () => {
                              try {
                                const shareText = `🌙 "${story?.title}" — a personalised bedtime story made with dreamybedtime.com ✨`;
                                // Try to share the illustration image as a file
                                if (illustration) {
                                  const res = await fetch(illustration);
                                  const blob = await res.blob();
                                  const file = new File([blob], 'story-illustration.jpg', { type: blob.type || 'image/jpeg' });
                                  const shareData = { title: story?.title, text: shareText, files: [file] };
                                  if (navigator.canShare && navigator.canShare(shareData)) {
                                    await navigator.share(shareData);
                                    return;
                                  }
                                }
                                // Fallback: share URL only
                                await navigator.share({ title: story?.title, text: shareText, url: window.location.href });
                              } catch (err) {
                                if (err.name !== 'AbortError') console.error('Share failed:', err);
                              }
                            }}
                            title="Share"
                          >
                            <i className="bi bi-share me-1"></i>Share
                          </button>
                        )}
                        {hasPaidPlan && (
                          <button
                            className="btn btn-sm rounded-pill px-3 fw-semibold"
                            style={{background:'white', color:'#6f42c1', fontSize:'12px'}}
                            onClick={handleRegenerate}
                            disabled={regenLoading}
                            title="Generate a different scene"
                          >
                            {regenLoading
                              ? <><span className="spinner-border spinner-border-sm me-1"></span>Generating…</>
                              : <><i className="bi bi-arrow-clockwise me-1"></i>New Scene</>}
                          </button>
                        )}
                      </div>
                    </div>
                    {hasPaidPlan && (
                      <div className="mt-2">
                        <div className="text-muted text-center mb-1" style={{fontSize:'11px', letterSpacing:'0.5px', textTransform:'uppercase'}}>
                          Illustration style
                        </div>
                        <div className="d-flex flex-wrap justify-content-center gap-1">
                          {ILLUSTRATION_STYLE_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => setIllustrationStyle(opt.value)}
                              className="btn btn-sm rounded-pill"
                              style={{
                                padding: '3px 11px', fontSize: '12px', lineHeight: 1.5,
                                background: illustrationStyle === opt.value ? '#6f42c1' : 'var(--card-bg)',
                                color: illustrationStyle === opt.value ? '#fff' : 'var(--text-main)',
                                border: `1px solid ${illustrationStyle === opt.value ? '#6f42c1' : 'var(--border-soft, #dee2e6)'}`,
                                transition: 'all 0.15s',
                              }}
                            >
                              {opt.icon} {opt.label}
                            </button>
                          ))}
                        </div>
                        <div className="text-muted text-center mt-1" style={{fontSize:'11px'}}>
                          Select a style then tap <strong>New Scene</strong> to regenerate
                        </div>
                      </div>
                    )}
                    {!hasPaidPlan && (
                      <div className="text-muted small mt-2 text-center">
                        <Link to="/pricing" style={{color:'#6f42c1'}}>Upgrade</Link> to regenerate a different scene
                      </div>
                    )}
                  </div>
                )}

                {illustrationStatus === 'failed' && (
                  <div className="mx-auto mb-4 d-flex flex-column align-items-center justify-content-center"
                    style={{width:'100%', maxWidth:'480px', height:'120px', borderRadius:'16px',
                      background:'#fff5f5', border:'1px dashed #f5c6cb'}}>
                    <span className="text-muted small">Illustration unavailable</span>
                    {hasPaidPlan && (
                      <button className="btn btn-sm btn-outline-secondary rounded-pill mt-2 px-3"
                        onClick={handleRegenerate} disabled={regenLoading}>
                        <i className="bi bi-arrow-clockwise me-1"></i>Try again
                      </button>
                    )}
                  </div>
                )}
              </div>

              {(speaking || aiPlaying || aiPaused || aiLoading || clonePlaying || clonePaused || cloneLoading) && (
                <div className="mb-4 rounded-3 px-3 py-2" style={{
                  background: clonePlaying || clonePaused || cloneLoading ? (darkMode ? 'rgba(234,88,12,0.15)' : '#fff7ed') : (darkMode ? 'rgba(111,66,193,0.18)' : '#f0e8ff'),
                  border: `1px solid ${clonePlaying || clonePaused || cloneLoading ? '#fed7aa' : '#c9a8ff'}`,
                }}>
                  {/* Status row */}
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="bi bi-volume-up-fill" style={{color: clonePlaying || clonePaused || cloneLoading ? '#ea580c' : '#6f42c1'}}></i>
                    <span className="small fw-semibold" style={{color: clonePlaying || clonePaused || cloneLoading ? '#ea580c' : '#6f42c1', flex:1}}>
                      {cloneLoading ? `Generating in ${voiceProfile?.name || 'your voice'}…`
                        : clonePaused ? `Paused — ${voiceProfile?.name || 'your voice'}`
                        : clonePlaying ? `Playing in ${voiceProfile?.name || 'your voice'} ✨`
                        : aiLoading ? 'Generating narration…'
                        : aiPaused ? 'Narration paused'
                        : aiPlaying ? 'Smart Narration playing ✨'
                        : speechPaused ? 'Read-aloud paused'
                        : 'Reading aloud…'}
                    </span>
                    {/* Progress time */}
                    {(aiPlaying || aiPaused || clonePlaying || clonePaused) && narrationDuration > 0 && (
                      <span className="small text-muted" style={{fontSize:'11px', flexShrink:0}}>
                        {formatTime(narrationCurrentTime)} / {formatTime(narrationDuration)}
                      </span>
                    )}
                  </div>
                  {/* Progress bar (AI + clone only) */}
                  {(aiPlaying || aiPaused || clonePlaying || clonePaused) && narrationDuration > 0 && (
                    <div className="progress mb-2" style={{height:'4px', borderRadius:'4px', background: darkMode ? 'rgba(255,255,255,0.1)' : '#e8d5ff', cursor:'pointer'}}
                      onClick={e => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const pct = (e.clientX - rect.left) / rect.width;
                        const newTime = pct * narrationDuration;
                        const audio = audioObjRef.current || cloneAudioObjRef.current;
                        if (audio) audio.currentTime = newTime;
                        setNarrationCurrentTime(newTime);
                      }}>
                      <div className="progress-bar" style={{
                        width: `${(narrationCurrentTime / narrationDuration) * 100}%`,
                        background: clonePlaying || clonePaused ? '#ea580c' : '#6f42c1',
                        borderRadius:'4px', transition:'width 0.5s linear',
                      }} />
                    </div>
                  )}
                  {/* Playback speed pills */}
                  {(aiPlaying || aiPaused || clonePlaying || clonePaused || speaking || speechPaused) && (
                    <div className="d-flex align-items-center gap-1">
                      <span className="small text-muted me-1" style={{fontSize:'11px'}}>Speed:</span>
                      {[0.75, 1, 1.25].map(s => (
                        <button key={s} onClick={() => changePlaybackSpeed(s)}
                          className="btn btn-sm rounded-pill px-2 py-0"
                          style={{
                            fontSize:'11px', lineHeight:'1.6',
                            background: playbackSpeed === s ? (clonePlaying || clonePaused ? '#ea580c' : '#6f42c1') : 'transparent',
                            color: playbackSpeed === s ? 'white' : (darkMode ? '#c9a8ff' : '#6f42c1'),
                            border: `1px solid ${playbackSpeed === s ? 'transparent' : (darkMode ? '#3d2a72' : '#c9a8ff')}`,
                          }}>
                          {s}x
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Resume notice */}
              {!aiPlaying && !aiPaused && !clonePlaying && !clonePaused && !speaking && resumePositionRef.current > 1 && (
                <div className="d-flex align-items-center gap-2 mb-3 px-3 py-2 rounded-3 small"
                  style={{background: darkMode ? 'rgba(111,66,193,0.12)' : '#f0e8ff', border:'1px solid #c9a8ff', color:'#6f42c1'}}>
                  <i className="bi bi-bookmark-fill flex-shrink-0"></i>
                  <span>Resume from <strong>{formatTime(resumePositionRef.current)}</strong> where you left off</span>
                  <button className="btn btn-sm btn-link p-0 ms-auto" style={{color:'#6f42c1', fontSize:'11px', textDecoration:'none'}}
                    onClick={() => { resumePositionRef.current = 0; API.put(`/stories/${id}/narration-position`, { position: 0 }).catch(() => {}); }}>
                    Clear
                  </button>
                </div>
              )}

              <div style={{lineHeight:'1.9', fontSize:`${fontSize}px`, color: darkMode ? '#e8d5ff' : '#2d1b69'}}>
                {story?.content?.split('\n\n').map((para, i) => {
                  const isActive = activeSentenceIdx === i && (aiPlaying || clonePlaying || speaking);
                  return (
                    <p key={i} className="mb-4" style={isActive ? {
                      background: darkMode ? 'rgba(111,66,193,0.22)' : 'rgba(111,66,193,0.09)',
                      borderLeft: '3px solid #6f42c1',
                      paddingLeft: '10px',
                      borderRadius: '4px',
                      transition: 'background 0.3s',
                    } : {paddingLeft: '0', borderLeft:'none'}}>
                      {para}
                    </p>
                  );
                })}
              </div>

              {story?.moralLesson && (
                <div className="mt-5 p-4 text-center rounded-3" style={{
                  background: darkMode ? 'rgba(111,66,193,0.15)' : 'linear-gradient(135deg,#f0e8ff,#e8d5ff)',
                  border: '2px dashed #c9a8ff',
                }}>
                  <div className="fs-2 mb-2">💫</div>
                  <p className="fw-bold mb-1" style={{color: darkMode ? '#e8d5ff' : '#2d1b69'}}>Tonight's lesson</p>
                  <p className="mb-0 fst-italic" style={{color:'#c9a8ff', fontSize:'17px'}}>"{story.moralLesson}"</p>
                </div>
              )}

              {/* ── Star rating ── */}
              <div className="mt-5 text-center">
                <p className="fw-semibold mb-3" style={{color: darkMode ? '#e8d5ff' : '#2d1b69', fontSize:'16px'}}>
                  {storyRating ? '⭐ Thanks for rating!' : 'Did you enjoy this story?'}
                </p>
                <div className="d-flex justify-content-center gap-2 mb-2">
                  {[1,2,3,4,5].map(star => (
                    <button
                      key={star}
                      onClick={() => handleRating(star)}
                      onMouseEnter={() => setRatingHover(star)}
                      onMouseLeave={() => setRatingHover(0)}
                      disabled={ratingSubmitting}
                      style={{
                        background: 'none', border: 'none', padding: '4px 2px',
                        fontSize: 'clamp(28px,7vw,40px)',
                        cursor: ratingSubmitting ? 'default' : 'pointer',
                        transform: (ratingHover || storyRating) >= star ? 'scale(1.2)' : 'scale(1)',
                        transition: 'transform 0.15s',
                        filter: (ratingHover || storyRating) >= star ? 'none' : 'grayscale(1) opacity(0.4)',
                      }}
                    >
                      ⭐
                    </button>
                  ))}
                </div>
                {(ratingHover > 0 || storyRating) && (
                  <p className="mb-0 fw-semibold" style={{color:'#6f42c1', fontSize:'14px', minHeight:'20px'}}>
                    {RATING_LABELS[ratingHover || storyRating]}
                  </p>
                )}
              </div>

              {/* ── After-reading quiz prompt ── */}
              {flags.storyQuiz && (
                <div className="mt-5">
                  {hasPaidPlan ? (
                    <div className="text-center p-4 rounded-4"
                      style={{background: darkMode ? 'rgba(111,66,193,0.18)' : 'linear-gradient(135deg,#f5f0ff,#ede5ff)', border:'2px solid var(--border-purple2)'}}>
                      <div style={{fontSize:44, marginBottom:8, lineHeight:1}}>🧠</div>
                      {quizSubmitted ? (
                        <>
                          <div className="fw-bold mb-1" style={{color:'var(--text-main)', fontSize:'20px', fontFamily:"'Fredoka One',cursive"}}>
                            Quiz complete! 🎉
                          </div>
                          <div style={{color:'var(--text-muted2)', fontSize:'14px', marginBottom:14}}>
                            {(() => {
                              const s = quiz?.filter((q,i) => quizAnswers[i] === q.correctAnswer).length ?? 0;
                              const t = quiz?.length ?? 3;
                              return s === t ? `Perfect score — ${s}/${t}! Amazing listening! 🌟` : `Score: ${s}/${t} — great effort!`;
                            })()}
                          </div>
                          <button className="btn btn-outline-secondary rounded-pill px-4"
                            style={{fontSize:13}}
                            onClick={() => { setQuizAnswers({}); setQuizSubmitted(false); quizSectionRef.current?.scrollIntoView({behavior:'smooth', block:'start'}); }}>
                            <i className="bi bi-arrow-clockwise me-1"></i>Retake Quiz
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="fw-bold mb-1" style={{color:'var(--text-main)', fontSize:'20px', fontFamily:"'Fredoka One',cursive"}}>
                            The End! Ready for the quiz?
                          </div>
                          <div style={{color:'var(--text-muted2)', fontSize:'14px', marginBottom:14}}>
                            {previousScore
                              ? `Last time: ${previousScore.score}/${previousScore.total} — can you do better?`
                              : '3 fun questions to see how well they listened 👂'}
                          </div>
                          <button className="btn rounded-pill px-5 fw-semibold"
                            style={{background:'linear-gradient(135deg,#6f42c1,#9b59b6)', color:'white', border:'none', fontSize:15, padding:'10px 32px'}}
                            onClick={() => {
                              quizSectionRef.current?.scrollIntoView({behavior:'smooth', block:'start'});
                              if (!quiz && !quizLoading) handleGenerateQuiz();
                            }}>
                            {quizLoading
                              ? <><span className="spinner-border spinner-border-sm me-2"></span>Generating…</>
                              : <><i className="bi bi-play-fill me-1"></i>{quiz ? 'Take the Quiz' : 'Start the Quiz'}</>}
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="text-center p-4 rounded-4"
                      style={{background:'var(--purple-section)', border:'1px solid var(--border-purple)'}}>
                      <div style={{fontSize:36, marginBottom:8}}>🧠</div>
                      <div className="fw-bold mb-1" style={{color:'var(--text-main)', fontSize:'17px'}}>Story Quiz — did they listen?</div>
                      <div style={{color:'var(--text-muted2)', fontSize:'14px', marginBottom:14}}>
                        Test comprehension with 3 fun questions about the story. Basic &amp; Family plans only.
                      </div>
                      <Link to="/pricing" className="btn btn-dream rounded-pill px-4">Upgrade to Unlock</Link>
                    </div>
                  )}
                </div>
              )}

              {/* ── Story complete — milestone, tomorrow's idea, share ── */}
              {celebrate && <Confetti />}
              {(readMilestone || tomorrowIdea) && (
                <div className="mt-5 pt-4" style={{borderTop:'1px dashed var(--border-purple)'}}>
                  {readMilestone && (
                    <div className="text-center p-4 rounded-3 mb-3" style={{background:'var(--yellow-section)', border:'1px solid var(--border-yellow)'}}>
                      <div style={{fontSize:'34px', marginBottom:'4px'}}>🎉</div>
                      <div className="fw-bold" style={{color:'var(--text-main)', fontSize:'18px', fontFamily:"'Fredoka One',cursive"}}>
                        That's {story?.child?.name ? `${story.child.name}'s` : 'your'} {ordinal(readMilestone)} story!
                      </div>
                      <div style={{color:'var(--text-muted2)', fontSize:'13px'}}>
                        What a reader — keep the adventures coming 💜
                      </div>
                    </div>
                  )}

                  {tomorrowIdea && (
                    <div className="d-flex align-items-start gap-3 p-3 rounded-3 mb-3" style={{background:'var(--purple-section)', border:'1px solid var(--border-purple)'}}>
                      <span style={{fontSize:'22px', lineHeight:1.2}}>🌙</span>
                      <div>
                        <div className="fw-bold" style={{color:'var(--text-main)', fontSize:'14px'}}>Tomorrow's story idea</div>
                        <div style={{color:'var(--text-muted2)', fontSize:'13px', lineHeight:1.6}}>{tomorrowIdea.conceptLine}</div>
                        <div style={{color:'var(--text-soft)', fontSize:'12px', marginTop:'2px'}}>It'll be waiting on your dashboard tomorrow ✨</div>
                      </div>
                    </div>
                  )}

                  <div className="text-center">
                    <button type="button" className="btn btn-dream rounded-pill px-4 fw-semibold" onClick={openShareModal}>
                      💜 Share this story with grandma
                    </button>
                  </div>

                  {/* Growth loop — fires only on a milestone (the peak "wow"): invite
                      a friend (free story for them + a free story for you) and gift. */}
                  {readMilestone && (
                    <div className="text-center mt-3 p-3 rounded-3" style={{background:'var(--green-section)', border:'1px solid var(--border-green)'}}>
                      <div style={{color:'var(--text-main)', fontSize:'14px', fontWeight:700}}>
                        Know a family who'd love this? 💜
                      </div>
                      <div style={{color:'var(--text-muted2)', fontSize:'12px', margin:'2px 0 10px'}}>
                        Send a friend a free story for their child — and earn a free story yourself.
                      </div>
                      <div className="d-flex gap-2 justify-content-center flex-wrap">
                        <Link to="/dashboard/settings#referral" className="btn btn-sm btn-dream rounded-pill px-3 fw-semibold" style={{fontSize:'13px'}}>
                          💜 Invite a friend
                        </Link>
                        {flags.giftStory && (
                          <Link to="/gift" className="btn btn-sm rounded-pill px-3 fw-semibold" style={{fontSize:'13px', background:'var(--card-bg)', border:'1.5px solid var(--border-purple2)', color:'#6f42c1'}}>
                            🎁 Gift a story
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Continuation chapters ── */}
              {continuations.map((cont, idx) => (
                <div key={idx} className="mt-5 pt-4" style={{borderTop:'2px dashed var(--border-purple2)'}}>
                  {cont.generationStatus === 'pending' ? (
                    <div className="text-center py-5">
                      <div style={{fontSize:48, marginBottom:12, lineHeight:1}}>🌙</div>
                      <div className="fw-bold mb-2" style={{color:'var(--text-main)', fontFamily:"'Fredoka One',cursive", fontSize:18}}>
                        Writing Chapter {idx + 2}…
                      </div>
                      <div style={{color:'var(--text-muted2)', fontSize:14}}>Adding more magic to the adventure…</div>
                      <div className="mt-3 d-flex justify-content-center gap-1">
                        {[0,1,2].map(i => (
                          <div key={i} style={{
                            width:8, height:8, borderRadius:'50%', background:'#6f42c1',
                            animation:'pulse 1.2s ease-in-out infinite',
                            animationDelay:`${i * 0.2}s`,
                          }} />
                        ))}
                      </div>
                    </div>
                  ) : cont.generationStatus === 'failed' ? (
                    <div className="text-center py-4">
                      <div style={{fontSize:36, marginBottom:8}}>😔</div>
                      <div className="fw-semibold mb-1" style={{color:'var(--text-main)'}}>Chapter generation failed</div>
                      <div style={{color:'var(--text-muted2)', fontSize:13, marginBottom:16}}>Your story credit has been refunded. Please try again.</div>
                      <button className="btn btn-outline-secondary rounded-pill px-4" onClick={handleContinueStory} disabled={contGenerating}>
                        Try again
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="text-center mb-4">
                        <span className="badge rounded-pill px-3 py-2" style={{
                          background:'var(--purple-section-2)',
                          color:'var(--text-main)',
                          fontSize:12,
                          fontWeight:600,
                          letterSpacing:'0.03em',
                        }}>
                          ✨ Chapter {idx + 2}
                        </span>
                        {cont.title && (
                          <h4 className="mt-3 mb-0 fw-bold" style={{
                            color:'var(--text-main)',
                            fontFamily:"'Fredoka One',cursive",
                            fontSize:24,
                          }}>
                            {cont.title}
                          </h4>
                        )}
                      </div>
                      <div style={{lineHeight:'1.9', fontSize:`${fontSize}px`, color: darkMode ? '#e8d5ff' : '#2d1b69'}}>
                        {(cont.content || '').split('\n\n').map((para, i) => (
                          <p key={i} className="mb-4">{para}</p>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}

              {/* ── "What happens next?" button ── */}
              {story?.generationStatus === 'ready' && (
                <div className="mt-5 text-center">
                  {contError && (
                    <div className="alert alert-warning rounded-3 mb-3" style={{fontSize:13}}>
                      {contError}
                    </div>
                  )}
                  <div className="p-4 rounded-4" style={{
                    background: darkMode ? 'rgba(111,66,193,0.12)' : 'linear-gradient(135deg,#f5f0ff,#ede5ff)',
                    border: '2px dashed var(--border-purple2)',
                  }}>
                    <div style={{fontSize:40, marginBottom:8, lineHeight:1}}>📖</div>
                    <div className="fw-bold mb-1" style={{color:'var(--text-main)', fontFamily:"'Fredoka One',cursive", fontSize:20}}>
                      {continuations.length === 0 ? 'Want to know what happens next?' : 'Keep the adventure going?'}
                    </div>
                    <div style={{color:'var(--text-muted2)', fontSize:14, marginBottom:20}}>
                      {continuations.length === 0
                        ? 'Generate a sequel chapter — each one uses 1 story credit.'
                        : `Chapter ${continuations.filter(c => c.generationStatus === 'ready').length + 2} is ready to be written — uses 1 story credit.`}
                    </div>
                    <button
                      className="btn btn-dream rounded-pill px-5 fw-semibold"
                      style={{fontSize:16, padding:'12px 36px'}}
                      onClick={handleContinueStory}
                      disabled={contGenerating}
                    >
                      {contGenerating
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Writing next chapter…</>
                        : <>✨ What happens next?</>}
                    </button>
                    <div style={{color:'var(--text-soft)', fontSize:11, marginTop:10}}>
                      Uses 1 story credit · Chapter {continuations.length + 2} of your adventure
                    </div>
                  </div>
                </div>
              )}

            </div>
            <div className="p-2" style={{background:'linear-gradient(135deg,#6f42c1,#2d1b69)'}}></div>
          </div>

          {/* ── Story Quiz ─────────────────────────────────────── */}
          {flags.storyQuiz && hasPaidPlan ? (
            <div ref={quizSectionRef} className="card border-0 shadow-sm mt-4" style={{borderRadius:'20px', overflow:'hidden'}}>
              <div className="p-3 d-flex align-items-center justify-content-between"
                style={{background:'linear-gradient(135deg,#2d1b69,#6f42c1)'}}>
                <div className="d-flex align-items-center gap-2">
                  <span className="fs-4">🧠</span>
                  <span className="fw-bold text-white" style={{fontFamily:"'Fredoka One',cursive", fontSize:'18px'}}>Story Quiz</span>
                  <span className="badge rounded-pill" style={{background:'rgba(255,255,255,0.2)', color:'white', fontSize:'11px'}}>3 questions</span>
                </div>
                {!quiz && (
                  <button
                    className="btn btn-sm rounded-pill px-4 fw-semibold"
                    style={{background:'white', color:'#6f42c1'}}
                    onClick={handleGenerateQuiz}
                    disabled={quizLoading}
                  >
                    {quizLoading
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Generating…</>
                      : <><i className="bi bi-play-fill me-1"></i>Start Quiz</>}
                  </button>
                )}
                {quiz && !quizSubmitted && (
                  <button
                    className="btn btn-sm rounded-pill px-4 fw-semibold"
                    style={{background:'white', color:'#6f42c1'}}
                    onClick={() => {
                      const score = quiz.filter((q, i) => quizAnswers[i] === q.correctAnswer).length;
                      trackQuizSubmitted(id, score, quiz.length);
                      setQuizSubmitted(true);
                      if (score === quiz.length) startCelebration();
                      // Record result + adapt reading level (server decides)
                      API.put(`/stories/${id}/quiz-result`, { score, total: quiz.length })
                        .then(res => {
                          if (res.data.levelChanged) {
                            setLevelUp({ direction: res.data.direction, newLevel: res.data.newLevel, childName: res.data.childName });
                            if (res.data.direction === 'up') startCelebration();
                          }
                          setPreviousScore({ score, total: quiz.length });
                        })
                        .catch(() => {});
                    }}
                    disabled={Object.keys(quizAnswers).length < quiz.length}
                  >
                    <i className="bi bi-check2-circle me-1"></i>Submit
                  </button>
                )}
                {quizSubmitted && (
                  <button
                    className="btn btn-sm rounded-pill px-4 fw-semibold"
                    style={{background:'rgba(255,255,255,0.2)', color:'white', border:'1px solid rgba(255,255,255,0.4)'}}
                    onClick={() => { setQuiz(null); setQuizAnswers({}); setQuizSubmitted(false); }}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>Retry
                  </button>
                )}
              </div>

              <div className="p-4">
                {!quiz && !quizLoading && (
                  <div className="text-center py-3">
                    <p className="text-muted mb-0" style={{fontSize:'14px'}}>
                      Test how well your little one listened! Get 3 fun questions about the story.
                    </p>
                  </div>
                )}

                {quizLoading && (
                  <div className="text-center py-4">
                    <div className="spinner-border mb-2" style={{color:'#6f42c1'}}></div>
                    <p className="text-muted small mb-0">Creating questions just for this story…</p>
                  </div>
                )}

                {quiz && (
                  <>
                    {quizSubmitted && (() => {
                      const score = quiz.filter((q, i) => quizAnswers[i] === q.correctAnswer).length;
                      const pct = Math.round((score / quiz.length) * 100);
                      const emoji = pct === 100 ? '🌟' : pct >= 66 ? '😊' : '💪';
                      const childFirst = story?.child?.name;
                      const msg = pct === 100
                        ? `Amazing! ${childFirst || 'You'} got ${score}/${quiz.length} right!`
                        : pct >= 66 ? `Great job${childFirst ? `, ${childFirst}` : ''}! Almost there!` : 'Good try! Let\'s read again tonight!';
                      return (
                        <div className="text-center rounded-3 p-4 mb-4"
                          style={{background: pct === 100 ? (darkMode ? 'rgba(5,150,105,0.15)' : '#f0fdf4') : pct >= 66 ? 'var(--purple-section)' : (darkMode ? 'rgba(217,119,6,0.15)' : '#fffbeb'),
                            border: `2px solid ${pct === 100 ? '#86efac' : pct >= 66 ? '#c9a8ff' : '#fde68a'}`}}>
                          <div className="fs-1 mb-2">{emoji}</div>
                          <div className="fw-bold fs-4 mb-1" style={{color:'var(--text-main)'}}>{score} / {quiz.length}</div>
                          <div className="fw-semibold" style={{color: pct === 100 ? '#059669' : pct >= 66 ? '#6f42c1' : '#d97706'}}>{msg}</div>
                        </div>
                      );
                    })()}

                    {/* Adaptive reading-level change */}
                    {levelUp && (
                      <div className="text-center rounded-3 p-4 mb-4" style={{
                        background: levelUp.direction === 'up' ? 'var(--green-section)' : 'var(--blue-section)',
                        border: `2px solid ${levelUp.direction === 'up' ? 'var(--border-green)' : 'var(--border-blue)'}`,
                      }}>
                        <div className="fs-1 mb-2">{levelUp.direction === 'up' ? '📈' : '🌱'}</div>
                        <div className="fw-bold mb-1" style={{color:'var(--text-main)', fontSize:'18px', fontFamily:"'Fredoka One',cursive"}}>
                          {levelUp.direction === 'up'
                            ? `${levelUp.childName}'s reading level went up!`
                            : `We've tuned ${levelUp.childName}'s stories`}
                        </div>
                        <div style={{color:'var(--text-muted2)', fontSize:'14px'}}>
                          {levelUp.direction === 'up'
                            ? `Quizzes are getting easy — stories are now Level ${levelUp.newLevel} for a bigger challenge 🎉`
                            : `We'll keep stories at Level ${levelUp.newLevel} so they stay just right 💜`}
                        </div>
                      </div>
                    )}

                    {!quizSubmitted && previousScore && (
                      <div className="text-center mb-3 px-3 py-2 rounded-3 small d-flex align-items-center justify-content-center gap-2"
                        style={{background:'var(--item-bg)', color:'var(--text-muted2)'}}>
                        <i className="bi bi-clock-history"></i>
                        Last time you scored {previousScore.score}/{previousScore.total} — can you beat it?
                      </div>
                    )}

                    <div className="d-flex flex-column gap-4">
                      {quiz.map((q, qi) => {
                        const chosen = quizAnswers[qi];
                        const isCorrect = chosen === q.correctAnswer;
                        return (
                          <div key={qi}>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="badge rounded-pill px-3 py-1"
                                style={{background:'var(--purple-section)', color:'#9b59b6', fontSize:'11px', fontWeight:600, letterSpacing:'0.04em'}}>
                                Question {qi + 1} of {quiz.length}
                              </span>
                              {quizAnswers[qi] !== undefined && !quizSubmitted && (
                                <span className="badge rounded-pill"
                                  style={{background:darkMode ? 'rgba(5,150,105,0.2)' : '#dcfce7', color:'#15803d', fontSize:'11px'}}>
                                  ✓ Answered
                                </span>
                              )}
                            </div>
                            <p className="fw-semibold mb-3" style={{color:'var(--text-main)', fontSize:'15px', lineHeight:1.4}}>
                              {q.question}
                            </p>
                            <div className="d-flex flex-column gap-2">
                              {q.options.map((opt, oi) => {
                                let bg = darkMode ? '#1a1438' : '#f8f5ff', border = darkMode ? '1.5px solid #3d2a72' : '1.5px solid #e8d5ff', color = darkMode ? '#e8d5ff' : '#2d1b69';
                                if (quizSubmitted) {
                                  if (oi === q.correctAnswer) { bg = darkMode ? 'rgba(5,150,105,0.15)' : '#f0fdf4'; border = '1.5px solid #86efac'; color = '#059669'; }
                                  else if (oi === chosen && chosen !== q.correctAnswer) { bg = darkMode ? 'rgba(220,38,38,0.15)' : '#fff5f5'; border = '1.5px solid #fca5a5'; color = '#dc2626'; }
                                } else if (chosen === oi) {
                                  bg = darkMode ? 'rgba(111,66,193,0.3)' : '#e8d5ff'; border = '1.5px solid #6f42c1'; color = darkMode ? '#c9a8ff' : '#6f42c1';
                                }
                                return (
                                  <button
                                    key={oi}
                                    className="btn text-start rounded-3 px-3 py-2 d-flex align-items-center gap-2"
                                    style={{background: bg, border, color, fontSize:'14px', transition:'all 0.15s'}}
                                    onClick={() => !quizSubmitted && setQuizAnswers(a => ({...a, [qi]: oi}))}
                                    disabled={quizSubmitted}
                                  >
                                    <span className="badge rounded-circle fw-bold flex-shrink-0"
                                      style={{background: quizSubmitted && oi === q.correctAnswer ? '#059669' : quizSubmitted && oi === chosen && chosen !== q.correctAnswer ? '#dc2626' : chosen === oi && !quizSubmitted ? '#6f42c1' : '#d8d0e8', color:'white', width:'24px', height:'24px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px'}}>
                                      {String.fromCharCode(65 + oi)}
                                    </span>
                                    {opt}
                                    {quizSubmitted && oi === q.correctAnswer && <i className="bi bi-check-circle-fill ms-auto" style={{color:'#059669'}}></i>}
                                    {quizSubmitted && oi === chosen && chosen !== q.correctAnswer && <i className="bi bi-x-circle-fill ms-auto" style={{color:'#dc2626'}}></i>}
                                  </button>
                                );
                              })}
                            </div>
                            {quizSubmitted && q.explanation && (
                              <div className="mt-2 px-3 py-2 rounded-3 small d-flex align-items-start gap-2"
                                style={{background: isCorrect ? (darkMode ? 'rgba(5,150,105,0.15)' : '#f0fdf4') : (darkMode ? 'rgba(220,38,38,0.15)' : '#fff5f5'), color: isCorrect ? '#059669' : '#dc2626'}}>
                                <i className={`bi ${isCorrect ? 'bi-lightbulb-fill' : 'bi-info-circle-fill'} flex-shrink-0 mt-1`}></i>
                                {q.explanation}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Bottom submit / progress ── shown below the questions for visibility */}
                    {!quizSubmitted && (
                      <div className="mt-4 text-center">
                        <div className="small mb-3" style={{color:'var(--text-muted2)'}}>
                          {Object.keys(quizAnswers).length} of {quiz.length} answered
                          {Object.keys(quizAnswers).length === quiz.length && (
                            <span style={{color:'#059669', marginLeft:6}}>— ready to submit!</span>
                          )}
                        </div>
                        <button
                          className="btn rounded-pill px-5 fw-semibold"
                          style={{
                            background: Object.keys(quizAnswers).length < quiz.length
                              ? 'var(--item-bg)' : 'linear-gradient(135deg,#059669,#10b981)',
                            color: Object.keys(quizAnswers).length < quiz.length ? 'var(--text-muted2)' : 'white',
                            border: 'none', fontSize:15, padding:'10px 28px', transition:'all 0.2s',
                          }}
                          disabled={Object.keys(quizAnswers).length < quiz.length}
                          onClick={() => {
                            const score = quiz.filter((q, i) => quizAnswers[i] === q.correctAnswer).length;
                            trackQuizSubmitted(id, score, quiz.length);
                            setQuizSubmitted(true);
                            if (score === quiz.length) startCelebration();
                            API.put(`/stories/${id}/quiz-result`, { score, total: quiz.length })
                              .then(res => {
                                if (res.data.levelChanged) {
                                  setLevelUp({ direction: res.data.direction, newLevel: res.data.newLevel, childName: res.data.childName });
                                  if (res.data.direction === 'up') startCelebration();
                                }
                                setPreviousScore({ score, total: quiz.length });
                              })
                              .catch(() => {});
                          }}
                        >
                          <i className="bi bi-check2-circle me-2"></i>Submit Answers
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : null}

          {/* Upgrade prompt for free users */}
          {!hasPaidPlan && (
            <div className="card border-0 mt-4 p-4 text-center" style={{borderRadius:'16px', background:'var(--purple-section)'}}>
              <div className="fs-3 mb-2">✨</div>
              <p className="fw-bold mb-1" style={{color:'var(--text-main)'}}>Want high-quality smart narration?</p>
              <p className="text-muted small mb-3">Upgrade to Basic or Family to unlock a warm, expressive narration voice for every story.</p>
              <Link to="/pricing" className="btn btn-dream rounded-pill px-4">Upgrade Plan</Link>
            </div>
          )}

          {/* ── Post-story reflection prompts ── */}
          {story && storyRating && (
            <ReflectionPrompts story={story} childName={story.child?.name || 'your child'} />
          )}

          {/* ── Wish list button — child can request next story ── */}
          {story?.child && (
            <div className="mt-4 d-flex justify-content-center">
              <WishlistButton childId={story.child?._id || story.child} childName={story.child?.name} />
            </div>
          )}

          {/* ── "What happens next?" choices ── */}
          {story?._id && story?.child && (
            <NextChoicesPanel storyId={story._id} childId={story.child?._id || story.child} />
          )}

          {/* ── Series continuation prompt ── */}
          {story?.seriesId && !seriesContinuationDismissed && (
            <SeriesContinuationCard
              story={story}
              onDismiss={() => setSeriesContinuationDismissed(true)}
            />
          )}

          {/* ── Tomorrow's story preview ── */}
          {story?.child && (
            <TomorrowPreviewCard childId={story.child?._id || story.child} />
          )}

          {/* Actions */}
          <div className="card border-0 shadow-sm mt-4 p-3" style={{borderRadius:'16px'}}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex gap-2 flex-wrap">
                <Link to="/dashboard/generate" className="btn btn-dream rounded-pill px-4">
                  <i className="bi bi-magic me-2"></i>Generate Another
                </Link>
                <Link to="/dashboard/library" className="btn btn-outline-secondary rounded-pill px-4">
                  View Library
                </Link>
                {flags.community && (story?.isPublished ? (
                  <button className="btn btn-sm btn-success rounded-pill px-3 fw-semibold" onClick={openPublishModal}>
                    <i className="bi bi-globe2 me-1"></i>Published
                  </button>
                ) : (
                  <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" onClick={openPublishModal}>
                    <i className="bi bi-share me-1"></i>Share to Community
                  </button>
                ))}
              </div>
              {/* deleteStory returns 403 for co-parents, so don't offer it. */}
              {!isViaCoParent(user) && (
                <button className="btn btn-outline-danger rounded-pill px-3 small" onClick={handleDelete}>
                  <i className="bi bi-trash me-1"></i>Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* ── Share Modal ─────────────────────────────────── */}
      {flags.grandparentSharing && showShareModal && (
        <div className="modal show d-block" style={{background:'rgba(0,0,0,0.55)', zIndex:1050}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0" style={{borderRadius:'20px'}}>
              <div className="modal-header border-0 pb-0 px-4 pt-4">
                <div>
                  <h5 className="modal-title fw-bold mb-0" style={{color:'var(--text-main)', fontFamily:"'Fredoka One',cursive"}}>
                    Share with Grandparents
                  </h5>
                  <p className="text-muted small mb-0">Anyone with the link can read the story — no account needed.</p>
                </div>
                <button className="btn-close ms-auto" onClick={() => setShowShareModal(false)}></button>
              </div>

              <div className="modal-body px-4 pb-4 pt-3">
                {shareLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" style={{color:'#6f42c1'}}></div>
                  </div>
                ) : shareLink ? (
                  <>
                    {/* ── Active link ── */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">Share link</label>
                      <div className="input-group">
                        <input type="text" className="form-control" readOnly value={shareLink.url}
                          style={{fontSize:'13px', background:'var(--header-bg)'}} />
                        <button className="btn btn-dream" onClick={copyShareUrl}>
                          {shareCopied
                            ? <><i className="bi bi-check2 me-1"></i>Copied!</>
                            : <><i className="bi bi-clipboard me-1"></i>Copy</>}
                        </button>
                      </div>
                      {shareLink.expiresAt && (
                        <p className="text-muted small mt-1 mb-0">
                          <i className="bi bi-clock me-1"></i>
                          Expires {new Date(shareLink.expiresAt).toLocaleDateString()}
                          {shareLink.hasPassword && <span className="ms-2"><i className="bi bi-lock-fill me-1"></i>Password protected</span>}
                        </p>
                      )}
                    </div>

                    {/* Share via */}
                    <div className="d-flex flex-wrap gap-2 mb-4">
                      <a href={`https://wa.me/?text=${encodeURIComponent(`🌙 "${story?.title}" — a personalised bedtime story starring ${story?.child?.name || 'my child'} ✨\n\nRead it here (no account needed): ${shareLink.url}`)}`}
                        target="_blank" rel="noreferrer"
                        className="btn btn-sm rounded-pill px-3 fw-semibold"
                        style={{background:'#25D366', color:'white'}}>
                        <i className="bi bi-whatsapp me-1"></i>WhatsApp
                      </a>
                      <a href={`sms:?body=${encodeURIComponent(`🌙 "${story?.title}" starring ${story?.child?.name || 'my child'} ✨\nRead it here: ${shareLink.url}`)}`}
                        className="btn btn-sm rounded-pill px-3 fw-semibold"
                        style={{background:'#3b82f6', color:'white'}}>
                        <i className="bi bi-chat-fill me-1"></i>iMessage / SMS
                      </a>
                      <a href={`mailto:?subject=${encodeURIComponent(`A bedtime story: ${story?.title}`)}&body=${encodeURIComponent(`Hi!\n\nI made a personalised bedtime story starring ${story?.child?.name || 'my child'} and wanted to share it with you.\n\nRead it here — no account needed:\n${shareLink.url}\n\nMade with Dreamy Bedtime ✨`)}`}
                        className="btn btn-sm btn-outline-secondary rounded-pill px-3">
                        <i className="bi bi-envelope me-1"></i>Email
                      </a>
                    </div>

                    {/* Stats */}
                    <div className="d-flex rounded-3 overflow-hidden mb-4" style={{background:'var(--purple-section)'}}>
                      <div className="text-center flex-fill py-3">
                        <div className="fw-bold fs-5" style={{color:'#6f42c1'}}>{shareLink.viewCount}</div>
                        <div className="text-muted small">Views</div>
                      </div>
                      {['❤️','🌟','😄'].map(e => {
                        const count = shareLink.reactions?.filter(r => r.emoji === e).length || 0;
                        return (
                          <div key={e} className="text-center flex-fill py-3 border-start">
                            <div className="fw-bold fs-5">{count || '—'}</div>
                            <div style={{fontSize:'16px', lineHeight:1}}>{e}</div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Update settings */}
                    <div className="p-3 rounded-3 mb-3" style={{background:'var(--header-bg)'}}>
                      <p className="small fw-semibold mb-2" style={{color:'var(--text-main)'}}>Update link settings</p>
                      <div className="row g-2">
                        <div className="col-7">
                          <select className="form-select form-select-sm" value={shareForm.expiryDays}
                            onChange={e => setShareForm(f => ({...f, expiryDays: e.target.value}))}>
                            <option value="30">Expires in 30 days</option>
                            <option value="90">Expires in 90 days</option>
                            <option value="">Never expires</option>
                          </select>
                        </div>
                        <div className="col-5">
                          <input type="password" className="form-control form-control-sm"
                            placeholder={shareLink.hasPassword ? 'Change password…' : 'Add password…'}
                            value={shareForm.password}
                            onChange={e => setShareForm(f => ({...f, password: e.target.value}))} />
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <button className="btn btn-dream rounded-pill px-4" onClick={handleCreateShareLink} disabled={shareLoading}>
                        <i className="bi bi-arrow-clockwise me-1"></i>Update
                      </button>
                      <button className="btn btn-outline-danger rounded-pill px-3" onClick={handleRevokeShareLink}>
                        <i className="bi bi-trash me-1"></i>Revoke Link
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* ── No link yet ── */}
                    <p className="text-muted small mb-4">
                      Create a private link to send to grandparents, relatives, or anyone you'd like.
                      They can read the story without needing a Dreamy Bedtime account.
                    </p>
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">Link expiry</label>
                      <select className="form-select" value={shareForm.expiryDays}
                        onChange={e => setShareForm(f => ({...f, expiryDays: e.target.value}))}>
                        <option value="30">30 days</option>
                        <option value="90">90 days</option>
                        <option value="">Never expires</option>
                      </select>
                    </div>
                    <div className="mb-4">
                      <label className="form-label fw-semibold small">
                        Password protect <span className="text-muted fw-normal">(optional)</span>
                      </label>
                      <input type="password" className="form-control" placeholder="Leave blank for no password"
                        value={shareForm.password}
                        onChange={e => setShareForm(f => ({...f, password: e.target.value}))} />
                    </div>
                    <button className="btn btn-dream btn-lg w-100 rounded-pill fw-bold"
                      onClick={handleCreateShareLink} disabled={shareLoading}>
                      {shareLoading
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Creating…</>
                        : <><i className="bi bi-link-45deg me-2"></i>Create Share Link</>}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Story Card Modal ───────────────────────────── */}
      {showCardModal && (
        <StoryCardModal story={story} onClose={() => setShowCardModal(false)} />
      )}

      {/* ── Publish to Community Modal ─────────────────── */}
      {flags.community && showPublishModal && (
        <div className="modal show d-block" style={{background:'rgba(0,0,0,0.55)', zIndex:1050}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0" style={{borderRadius:'20px'}}>
              <div className="modal-header border-0 pb-0 px-4 pt-4">
                <div>
                  <h5 className="modal-title fw-bold mb-0" style={{color:'var(--text-main)', fontFamily:"'Fredoka One',cursive"}}>
                    {story?.isPublished ? '🌍 Story Published' : '🌍 Share to Community'}
                  </h5>
                  <p className="text-muted small mb-0">
                    {story?.isPublished
                      ? 'Your story is live in the community library.'
                      : 'Share this story with families around the world — anonymously.'}
                  </p>
                </div>
                <button className="btn-close ms-auto" onClick={() => setShowPublishModal(false)}></button>
              </div>

              <div className="modal-body px-4 pb-4 pt-3">
                {story?.isPublished ? (
                  <>
                    <div className="rounded-3 p-3 mb-4 d-flex align-items-center gap-3" style={{background:'#f0fdf4', border:'1px solid #86efac'}}>
                      <i className="bi bi-check-circle-fill fs-4" style={{color:'#059669'}}></i>
                      <div>
                        <div className="fw-semibold small" style={{color:'#059669'}}>Live in the community library</div>
                        <Link to={`/community/${story.publishSlug}`} target="_blank" rel="noreferrer"
                          className="small" style={{color:'#6f42c1'}}>/community/{story.publishSlug}</Link>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <Link to={`/community/${story.publishSlug}`} className="btn btn-dream rounded-pill px-4" target="_blank">
                        <i className="bi bi-box-arrow-up-right me-1"></i>View Public Page
                      </Link>
                      <button className="btn btn-outline-danger rounded-pill px-4" onClick={handleTogglePublish} disabled={publishLoading}>
                        {publishLoading
                          ? <><span className="spinner-border spinner-border-sm me-1"></span>Removing…</>
                          : <><i className="bi bi-eye-slash me-1"></i>Unpublish</>}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rounded-3 p-3 mb-4" style={{background:'var(--purple-section)', border:'1px solid #c9a8ff'}}>
                      <p className="small mb-0" style={{color:'#6f42c1'}}>
                        <i className="bi bi-shield-check me-1"></i>
                        <strong>Privacy first:</strong> Only the name below and the story content are shared. Your account, email, and child's full identity stay private.
                      </p>
                    </div>
                    <div className="mb-4">
                      <label className="form-label fw-semibold small">Display name for your child</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Aria, 4"
                        maxLength={40}
                        value={publishName}
                        onChange={e => setPublishName(e.target.value)}
                      />
                      <div className="form-text">This is the only name shown publicly. You can use a nickname.</div>
                    </div>
                    <button className="btn btn-dream btn-lg w-100 rounded-pill fw-bold"
                      onClick={handleTogglePublish} disabled={publishLoading || !publishName.trim()}>
                      {publishLoading
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Publishing…</>
                        : <><i className="bi bi-globe2 me-2"></i>Publish to Community</>}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <TopUpModal
        show={showTopUp}
        onClose={() => setShowTopUp(false)}
        packType={topUpType}
        planName={entitledPlan(user)}
      />
    </DashboardLayout>
  );
};

export default StoryReaderPage;
